import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaymentDraftService } from './features/payment-draft.service';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import {
  DensityService,
  EntitlementContextService,
  InsightsModeService,
  NotificationService,
  StepUpCeremonyService,
  TbAdvisoryBannerComponent,
  EducationModeService,
  EntitlementModeService,
  TbMobileNavComponent,
  TbNotificationToastComponent,
  TbThemeImageComponent,
  TbTooltipDirective,
  ThemePreference,
  ThemeService,
  TB_MASKING_PREFERENCE,
  actionLabel,
  isExclusiveBankStaffRoles,
} from '@toybank/ui';
import { TbStepUpDialogComponent } from '@toybank/ui/step-up';
import { TbInsightsToggleComponent } from '@toybank/ui/insights';
import { TbNotificationBellComponent } from '@toybank/ui/notifications';
import { TranslocoPipe } from '@jsverse/transloco';
import { ApiService, OrgRef, type Me } from './api.service';
import { actingContextSkipsFactorProbe } from './acting-context.util';
import { AppStore } from './store';
import { ShellOrgSelectorComponent } from './shell-org-selector.component';
import { type DebugDrawersController, TB_DEBUG_DRAWERS } from './debug/debug-drawers.model';
import { EmbedService } from './embed/embed.service';
import { isDebugBuild } from './debug-flag';
import { AppLang, LanguageService } from './i18n/language.service';
import { OrganizationSegmentService } from './organization-segment.service';
import { PasskeyDetectionService } from './passkey-detection.service';
import { CosessionConsentBannerComponent } from './cosession/cosession-consent-banner.component';
import { CosessionDiscoveryService } from './cosession/cosession-discovery.service';
import { CosessionConsentAttentionService } from './cosession/cosession-consent-attention.service';
import { CosessionActiveBannerComponent } from './cosession/cosession-active-banner.component';
import { CosessionRemoteCursorComponent } from './cosession/cosession-remote-cursor.component';
import { CosessionApprovalModalComponent } from './cosession/cosession-approval-modal.component';
import { CosessionNavToastComponent } from './cosession/cosession-nav-toast.component';
import { FactorExpiryBannerComponent } from './features/security/factor-expiry-banner.component';
import { RecoveryModeBannerComponent } from './features/security/recovery-mode-banner.component';
import { TownsimProvenanceBannerComponent } from './townsim/townsim-provenance-banner.component';
import { isConfiguredSimHost } from './demo/persona-switch.model';
import type { TownsimSessionSelection } from './api.service';
import { FactorEnrollmentGateService } from './features/security/factor-enrollment-gate.service';
import { FactorService } from './features/security/factor.service';
import { TbCommandBoxComponent } from './assist/tb-command-box.component';
import { AssistInvitationService } from './assist/assist-invitation.service';
import { TbCommandIntentModalComponent } from './assist/tb-command-intent-modal.component';
import { TbAssistEntitlementQueryPanelComponent, type AssistEntitlementQueryCompleted } from './assist/tb-assist-entitlement-query-panel.component';
import { TbAssistInsightsPanelComponent } from './assist/tb-assist-insights-panel.component';
import { TbAssistRmReadPanelComponent } from './assist/tb-assist-rm-read-panel.component';
import { TbAssistAccountReadPanelComponent } from './assist/tb-assist-account-read-panel.component';
import { TbAssistHistoryDrawerComponent } from './assist/tb-assist-history-drawer.component';
import { AssistHistoryService, type AssistHistoryOutcome } from './assist/assist-history.service';
import { AssistThreadService, THINKING_SLOW_HINT_SECONDS } from './assist/assist-thread.service';
import { TbAssistThreadComponent } from './assist/tb-assist-thread.component';
import { TbAssistQueryPanelComponent } from './assist/tb-assist-query-panel.component';
import { AssistQueryService } from './assist/assist-query.service';
import { insightsTurnOutcome } from './assist/insights-turn-outcome';
import { explainDecisionErrorMessage } from './assist/explain-decision-error';
import type { AssistQueryResponse } from './api.service';
import { isEntitlementQueryKind } from './assist/assist-entitlement-query-intent.service';
import { GuideHighlightService } from './assist/guide-highlight.service';
import { TbGuideHighlightComponent } from './assist/tb-guide-highlight.component';
import { SpotlightHostComponent } from './assist/spotlight-host.component';
import { TbEmphasisArrowComponent } from './assist/tb-emphasis-arrow.component';
import { GuidedOnboardingChecklistComponent } from './features/guided-onboarding/guided-onboarding-checklist.component';
import { GuidedOnboardingService } from './features/guided-onboarding/guided-onboarding.service';
import { TbIdleWarningComponent } from './session/tb-idle-warning.component';
import { DemoPersonaSwitchService, type DemoSession } from './demo/demo-persona-switch.service';
import { DemoBootstrapGateComponent } from './demo/demo-bootstrap-gate.component';
import { DemoPersonaMapComponent } from './demo/demo-persona-map.component';
import { DemoPersonaLandingComponent } from './demo/demo-persona-landing.component';
import { EntitlementCapabilityService } from './entitlement-capability.service';
import { CustomerSurfaceAccessService } from './customer-surface-access.service';
import { ConsolidationConsentStore } from './features/consolidation-consent/consolidation-consent.store';
import {
  CapabilityAvailabilityService,
  ACH_COLLECTIONS_CAPABILITY_KEY,
  IMPLEMENTATIONS_CAPABILITY_KEY,
  INSIGHTS_ASK_CAPABILITY_KEY,
  PAYMENT_ORDERS_CAPABILITY_KEY,
  INSTANT_PAYMENTS_CAPABILITY_KEY,
  REPORT_DISTRIBUTION_CAPABILITY_KEY,
} from './capability-availability.service';
import { ENGINE_ACTION } from './entitlement-codes';
import { routeServices } from './route-services';
import {
  LOGIN_PRESENTATION,
  type LoginPresentation,
  type LoginSubmitStrategy,
} from '@toybank/ui';
import { PortalLoginScreenComponent } from './login/portal-login-screen.component';
import { PersonaGateOutletComponent } from './persona-gate-outlet.component';
import { LoginNoticeService } from '../../../shared/login-notice/login-notice.service';
import {
  PersonaSwitcher,
  TB_PERSONA_SWITCHER,
  personaSwitchAppliesToHost,
} from './demo/persona-switch.model';
import {
  consumePostLoginReturnUrl,
  isNonResumableSpaUrl,
  safePostLoginUrl,
} from './post-login-redirect';
import {
  AssistDecision,
  GuideRef,
  HelpArticleRef,
  ResolveIntentResponse,
  isClarifyResponse,
  isGuideResponse,
  isHelpResponse,
  isAccountReadKind,
  isInsightsResponse,
  isRmReadKind,
} from './assist/assist.model';
import { assistantSafeText, displayUserCommand } from './account-identifier-presentation';

// Dark/dense wave: theme icon + label maps used in the shell top bar.
const THEME_ICON: Record<ThemePreference, string> = {
  dark: 'dark_mode',
  light: 'light_mode',
  system: 'brightness_auto',
};

const THEME_LABEL: Record<ThemePreference, string> = {
  dark: 'Switch to light theme',
  light: 'Switch to system theme',
  system: 'Switch to dark theme',
};

export function isDeferredFactorEnrollmentRoute(url: string): boolean {
  const path = url.split(/[?#]/, 1)[0];
  return (
    path === '/security/hardware-token/order' ||
    path.startsWith('/security/orders/') ||
    // The dedicated enrollment screen itself must not be redirected away from (else a loop).
    path === '/login/enroll-factor' ||
    // ADR-0031 recovery ladder: the enrollment screen's verify-failure escape hatch links to
    // /recovery/login ("can't complete this on this device"). The gate must not chase the user
    // back out of the recovery flow. NOT a bypass: recovery grants no banking surface, and the
    // gate re-engages the moment any ordinary app route is visited.
    path === '/recovery' ||
    path.startsWith('/recovery/')
  );
}

/**
 * Post-login resume decision (pure — pinned in app.component.spec.ts).
 *
 * `stashed` is the consumed deep-link stash from authShellGuard's pre-auth bounce (already
 * validated by consumePostLoginReturnUrl). It only wins when the session landed somewhere
 * default or broken — the clean landings ('/', '/home') or a non-resumable URL (e.g. the debug
 * host's /signed-out resume bug below). If the BFF's saved-request already resumed a REAL deep
 * link (full-page unauthenticated hit → OIDC → back), the current URL wins and the stash is
 * dropped. Without a stash this reduces to the original non-resumable → /home sanitizer.
 */
export function postLoginResumeUrl(stashed: string | null, currentUrl: string): string | null {
  const landedOnDefault =
    currentUrl === '/' || currentUrl === '/home' || isNonResumableSpaUrl(currentUrl);
  if (stashed && landedOnDefault) return stashed;
  if (isNonResumableSpaUrl(currentUrl)) return '/home';
  return null;
}

/**
 * Re-exported so existing `import { actingContextSkipsFactorProbe } from './app.component'`
 * call sites (and app.component.spec.ts's direct unit tests of it) keep working. The canonical
 * definition now lives in `acting-context.util.ts` (live-journeys triage #3) so the OTHER
 * ungated call sites (factor-expiry banner, security hub, first-login enrollment) can import the
 * shared guard without a circular dependency back onto this file, which renders several of them.
 */
export { actingContextSkipsFactorProbe };

export function demoOrgNameFromSession(
  session: Pick<DemoSession, 'orgName' | 'companyName'> | null | undefined,
): string | null {
  const orgName = session?.orgName?.trim();
  if (orgName) return orgName;
  const companyName = session?.companyName?.trim();
  return companyName || null;
}

export function orgDisplayLabel(
  org: OrgRef,
  demoOrgName: string | null | undefined,
  activeOrgId: string | null | undefined,
): string {
  const name = demoOrgName?.trim();
  if (name && org.orgId === activeOrgId) return name;
  return org.displayName ?? org.orgId;
}

/**
 * UI-only visibility hint identifying the public demo host family (demo.localhost and
 * subdomains). Originally gated the now-removed "Just explore the demo" quickstart button;
 * still used for (1) app.config.ts's TB_BFF_AUTH_REDIRECT_ENABLED override, which keeps the
 * SPA's own branded login screen in front of demo-host visitors instead of an immediate bounce
 * to bare Keycloak, and (2) logout()'s reason label, which the signed-out page uses to show
 * demo-appropriate copy. EnvModeFilter (edge-bff) remains the actual security boundary either way.
 */
export function isPublicDemoHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'demo.localhost' || host.startsWith('demo.');
}

/**
 * The only browser-visible TownSim identity is an opaque member handle. Keep this deliberately
 * narrower than `agencyMode === 'PERSONA'`: demo personas retain their raw subject and must keep
 * using the ordinary effective-access self-read instead of Town's delegated display projection.
 */
export function isOpaqueTownsimMe(me: Me | null | undefined): boolean {
  return me?.agencyMode === 'PERSONA'
    && typeof me.memberHandle === 'string'
    && me.memberHandle.trim().length > 0
    && (me.sub === null || me.sub === undefined)
    && (me.effectiveSubject === null || me.effectiveSubject === undefined)
    && (me.effectiveOrgId === null || me.effectiveOrgId === undefined);
}

/**
 * Routes that must render WITHOUT an authenticated BFF session.
 *
 * BUG FIX: the <router-outlet /> must not gate inside the authenticated branch alone, or
 * else when logged out every "public" route falls through to the login landing and
 * the outlet never mounts (e.g. /recovery/login shows the sign-in screen, not the
 * RecoveryLoginComponent triage UI). These prefixes mark the pre-auth surfaces that the
 * shell renders in a minimal unauthenticated shell instead.
 *
 * Source of truth: app.routes.ts entries explicitly annotated "publicly accessible"
 * / "no auth guard" / "pre-auth":
 *   - /recovery/login           RecoveryLoginComponent          (ADR-0031, no auth guard)
 *   - /recovery/grant/:token    RecoveryGrantLandingComponent   (ADR-0031, no auth guard)
 *   - /recovery/enroll          RecoveryEnrollComponent         (mid-recovery; its own
 *                                                                 active-recovery-session guard)
 *   - /login/hrd                HrdLoginComponent               (ADR-0036 E1, pre-auth)
 *   - /login/enroll-factor      FirstLoginEnrollmentComponent   (post-auth, but reachable while
 *                                                                 store.user() is briefly null
 *                                                                 during the factor-gate redirect)
 *   - /sign-up/invitation/:token InvitationSignupComponent      (publicly accessible)
 * Matched by path PREFIX so child/param routes are covered.
 */
const PUBLIC_SHELL_ROUTE_PREFIXES = [
  '/signed-out',
  '/recovery',
  '/login/hrd',
  '/login/enroll-factor',
  '/sign-up/invitation',
  // KYC front door (notes/kyc-front-door/): /open renders P0-A3 unauthenticated, mirroring
  // auth-shell.guard.ts's PUBLIC_PATH_PREFIXES. Same prefix-with-slash-boundary matching, so
  // '/openX' does not match.
  '/open',
] as const;

export function isPublicShellRoute(url: string): boolean {
  const path = url.split(/[?#]/, 1)[0];
  return PUBLIC_SHELL_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + '/'),
  );
}

/**
 * The /open pages that exist only BEFORE sign-in (P0 "who is this for", P1 checklist, A2 consent):
 * nothing on them reads the user, so the boot `/api/me` probe is skipped there -- on a signed-out
 * visit it only ever answered 401, which every xt explorer run logged as console noise (lane
 * spa-open-4, UX fix 10). Exact paths only: A3 (`/open/verify`), the invite landing and every
 * signed-in module keep the probe.
 */
const SIGNED_OUT_ONLY_OPEN_PATHS = new Set(['/open', '/open/prepare', '/open/consent']);

export function skipsBootSessionProbe(url: string): boolean {
  const path = url.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';
  return SIGNED_OUT_ONLY_OPEN_PATHS.has(path);
}

export function shellUrlFromNavigationEvent(event: unknown, fallbackUrl: string): string | null {
  if (event instanceof NavigationStart) return event.url;
  if (event instanceof NavigationEnd) return event.urlAfterRedirects;
  if (event instanceof NavigationCancel || event instanceof NavigationError) return fallbackUrl;
  return null;
}

/** Build the assistant-authored GUIDE follow-up without carrying a full account identifier. */
export function assistantGuideQuery(response: ResolveIntentResponse): string {
  const summary = assistantSafeText(
    response.naturalLanguageSummary ?? response.actionKind ?? '',
    response.params,
  );
  return summary.toLowerCase().startsWith('how')
    ? summary
    : `how do I ${summary.toLowerCase()}`;
}

@Component({
  selector: 'tb-root',
  imports: [
    RouterOutlet,
    PersonaGateOutletComponent,
    RouterLink,
    RouterLinkActive,
    ShellOrgSelectorComponent,
    TranslocoPipe,
    PortalLoginScreenComponent,
    TbInsightsToggleComponent,
    TbMobileNavComponent,
    TbNotificationBellComponent,
    TbNotificationToastComponent,
    TbAdvisoryBannerComponent,
    CosessionConsentBannerComponent,
    CosessionActiveBannerComponent,
    CosessionRemoteCursorComponent,
    CosessionApprovalModalComponent,
    CosessionNavToastComponent,
    FactorExpiryBannerComponent,
    RecoveryModeBannerComponent,
    TownsimProvenanceBannerComponent,
    TbStepUpDialogComponent,
    TbThemeImageComponent,
    TbCommandBoxComponent,
    TbCommandIntentModalComponent,
    TbAssistEntitlementQueryPanelComponent,
    TbAssistInsightsPanelComponent,
    TbAssistRmReadPanelComponent,
    TbAssistAccountReadPanelComponent,
    TbAssistHistoryDrawerComponent,
    TbAssistThreadComponent,
    TbAssistQueryPanelComponent,
    TbGuideHighlightComponent,
    SpotlightHostComponent,
    TbEmphasisArrowComponent,
    GuidedOnboardingChecklistComponent,
    TbIdleWarningComponent,
    DemoBootstrapGateComponent,
    DemoPersonaMapComponent,
    DemoPersonaLandingComponent,
    TbTooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (sessionLoading()) {
      <p class="center tb-muted">{{ 'login.checkingSession' | transloco }}</p>
    } @else if (demoGateVisible()) {
      @defer (on immediate) {
      <tb-demo-bootstrap-gate
        [state]="demoPersonaSwitcher.bootstrapState()"
        [provisioning]="demoPersonaSwitcher.provisioning()"
        [progress]="demoPersonaSwitcher.provisioningProgress()"
        [retrying]="demoPersonaSwitcher.preparationRetryScheduled()"
        [correlationId]="demoPersonaSwitcher.bootstrapCorrelationId()"
        [errorMessage]="demoPersonaSwitcher.bootstrapError()"
        [recoveryRequired]="demoPersonaSwitcher.recoveryRequired()"
        (retryRequested)="retryDemoBootstrap()"
        (signOutRequested)="logout()"
      />
      }
    } @else if (isPublicRoute() && (!store.user() || currentUrl().startsWith('/open'))) {
      <!--
        BUG FIX: minimal unauthenticated shell for PUBLIC (pre-auth) routes.
        The <router-outlet /> must not live only inside the authenticated branch, or else
        when logged out the login landing renders and public routes (e.g. /recovery/login,
        /login/hrd, /sign-up/invitation/:token) never mount. This branch renders just the
        brand header + a centered container around the outlet — NO left nav, NO portal chrome,
        NO authenticated-only widgets (those depend on store.user()/orgs and would NPE).
      -->
      <div class="public-shell" data-testid="public-shell">
        <header class="public-bar">
          <!-- Theme-swapped (2026-09-20 light-mode visibility fix): .public-bar's own background
               is var(--tb-surface-panel), which flips near-white in the light theme -- the old
               single light-ink <img> rendered invisibly there. See TbThemeImageComponent. -->
          <tb-theme-image
            srcDark="brand/bank-of-antarctica-cashbro-wordmark-light.svg"
            srcLight="brand/bank-of-antarctica-cashbro-wordmark.svg"
            alt="Bank of Antarctica: CashBro"
            imgStyle="height: 1.75rem; width: auto;"
          />
        </header>
        <main class="public-content">
          <router-outlet />
        </main>
      </div>
    } @else if (!store.user()) {
      <!--
        SAME shared login screen the debug wrapper's (ADR-0105) centre pane renders --
        <tb-login-screen> (login/login-screen.component.ts). Only the submit strategy differs
        (redirect to Keycloak vs POST /bff/debug/login) plus a handful of surface-local
        presentation inputs (label/testid text) -- owner ruling 2026-08-18: the debug wrapper
        must not have its own alternate login page; this IS that page.

        demo99: single clean primary "Sign in" entry. The Keycloak login screen is now
        passkey-first (leads with "Sign in with a passkey", hides password behind "Use a
        password instead"), so this splash no longer needs its own passkey vs password
        distinction. login() -> /oauth2/authorization/keycloak (optionally carrying the typed
        username as a login_hint, edge-bff SecurityConfig#authRequestResolver) lands on that
        passkey-first KC screen.

        Owner directive (2026-08-18): the anonymous "Just explore the demo" quickstart
        affordance is REMOVED -- every user, including on the public demo host, signs in
        through real Keycloak OIDC. See isPublicDemoHost's docstring for what still reads the
        public-demo-host flag now that the quickstart button itself is gone, and app.config.ts
        for the (unchanged) reason TB_BFF_AUTH_REDIRECT_ENABLED stays off on demo hosts.
      -->
      @defer (on immediate) {
      <tb-portal-login-screen
        [presentation]="loginPresentation"
        [tagline]="(isPublicDemoHost ? 'login.taglineDemo' : 'login.tagline') | transloco"
        (usernameSubmitted)="loginUsername.set($event)"
        [usernameLabel]="'login.username' | transloco"
        [actionLabel]="'login.signIn' | transloco"
        [recoveryLinkLabel]="'login.cantSignIn' | transloco"
        [strategy]="loginSubmitStrategy"
        [notice]="loginNotices.notice()"
      />
      }
    } @else {
      <div class="shell" [class.nav-open]="navOpen()">
        <!-- F38: skip-to-main link -- FIRST focusable element in the authenticated shell, visible
             only on focus. Jumps keyboard/AT users past the top bar + left-nav straight to the
             routed page content (#main-content below), instead of forcing a tab-through of the
             switcher/actions/nav on every single page load. -->
        <a class="skip-link" href="#main-content" data-testid="skip-to-main">Skip to main content</a>
        <!-- ── Top bar ── -->
        <header class="bar">
          <div class="bar-left">
            <!-- Hamburger trigger (visible ≤ md, hidden at desktop) -->
            <tb-mobile-nav
              label="CashBro navigation"
              (navToggled)="navOpen.set($event)"
              data-testid="mobile-nav"
            >
              <nav class="nav" [attr.aria-label]="'nav.ariaPrimary' | transloco">
                <!--
                  ENTITLEMENT-DRIVEN NAV (PBAC+ReBAC demo-persona differentiation). Each section/link
                  is gated by capabilities.can(CODE) against the engine-fed capability snapshot
                  (NEVER Keycloak realm roles). Overview is ALWAYS present (owner rule: everyone lands
                  on Overview; it just contains different things). Universal sections (Insights/
                  Notifications, New Business, Settings, Support) stay visible to all personas.
                  Codes mirror entitlement-codes.ts ROUTE_ENTITLEMENT so nav visibility never diverges
                  from the not-entitled-in-place panel (Phase 3).
                -->
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.overview' | transloco }}</span>
                  <a routerLink="/home" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" data-testid="nav-home">{{ 'nav.home' | transloco }}</a>
                </div>
                @if (capabilities.can(CODES.VIEW_ACCOUNT) || capabilities.can(CODES.VIEW_CONSOLIDATED) || consolidationConsents.available()) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.accountsCash' | transloco }}</span>
                  @if (capabilities.can(CODES.VIEW_ACCOUNT)) {
                  <a routerLink="/accounts" routerLinkActive="on" data-testid="nav-accounts">{{ 'nav.accounts' | transloco }}</a>
                  <a routerLink="/cash" routerLinkActive="on" data-testid="nav-cash-position">{{ 'nav.cashPosition' | transloco }}</a>
                  }
                  @if (consolidationConsents.available()) {
                  <a routerLink="/consolidation-consents" routerLinkActive="on" data-testid="nav-consolidation-consents">{{ 'nav.consolidationConsent' | transloco }}</a>
                  }
                  <!-- ADR-0131: independent grant (VIEW_CONSOLIDATED) — not implied by VIEW_ACCOUNT,
                       mirrors ROUTE_ENTITLEMENT['consolidated-reporting'] so nav never diverges
                       from the in-place not-entitled panel. -->
                  @if (capabilities.can(CODES.VIEW_CONSOLIDATED)) {
                  <a routerLink="/consolidated-reporting" routerLinkActive="on" data-testid="nav-consolidated-reporting">{{ 'nav.consolidatedReporting' | transloco }}</a>
                  }
                </div>
                }
                @if (capabilities.can(CODES.INITIATE_PAYMENT) || capabilities.can(CODES.APPROVE_PAYMENT) || capabilities.can(CODES.SCHEDULE_PAYMENT) || capabilities.can(CODES.MANAGE_TEMPLATES) || capabilities.can(CODES.MANAGE_BENEFICIARIES) || capabilities.can(CODES.PP_DECIDE_EXCEPTION) || capabilities.can(CODES.PP_CONFIGURE)) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.payments' | transloco }}</span>
                  @if (capabilities.can(CODES.INITIATE_PAYMENT)) {
                  <a routerLink="/bills" routerLinkActive="on" data-testid="nav-bills">{{ 'nav.bills' | transloco }}</a>
                  <a routerLink="/pay" routerLinkActive="on" data-testid="nav-pay">{{ 'nav.pay' | transloco }}</a>
                  @if (!capabilityAvailability.isUnavailable(INSTANT_PAYMENTS_CAPABILITY_KEY)) {
                  <a routerLink="/payments/instant-payments" routerLinkActive="on" data-testid="nav-instant-payments">{{ 'nav.instantPayments' | transloco }}</a>
                  }
                  @if (!capabilityAvailability.isUnavailable(PAYMENT_ORDERS_CAPABILITY_KEY)) {
                  <a routerLink="/payments/orders" routerLinkActive="on" data-testid="nav-payment-orders">{{ 'nav.paymentOrders' | transloco }}</a>
                  }
                  @if (!capabilityAvailability.isUnavailable(ACH_COLLECTIONS_CAPABILITY_KEY)) {
                  <a routerLink="/payments/ach-collections" routerLinkActive="on" data-testid="nav-ach-collections">{{ 'nav.achCollections' | transloco }}</a>
                  }
                  <a routerLink="/payments/disbursement" routerLinkActive="on" data-testid="nav-disbursement">{{ 'nav.disbursement' | transloco }}</a>
                  <a routerLink="/payments/gpi-tracker" routerLinkActive="on" data-testid="nav-gpi-tracker">{{ 'nav.gpiTracker' | transloco }}</a>
                  <a routerLink="/batch" routerLinkActive="on" data-testid="nav-bulk-payment">{{ 'nav.bulkPayment' | transloco }}</a>
                  <a routerLink="/iso20022-import" routerLinkActive="on">{{ 'nav.iso20022Import' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.INITIATE_PAYMENT) || capabilities.can(CODES.APPROVE_PAYMENT)) {
                  <a routerLink="/payments/recalls" routerLinkActive="on" data-testid="nav-recalls">{{ 'nav.recalls' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.SCHEDULE_PAYMENT)) {
                  <a routerLink="/scheduled" routerLinkActive="on" data-testid="nav-scheduled">{{ 'nav.scheduled' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.MANAGE_TEMPLATES)) {
                  <a routerLink="/templates" routerLinkActive="on">{{ 'nav.templates' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.MANAGE_BENEFICIARIES)) {
                  <a routerLink="/beneficiaries" routerLinkActive="on" data-testid="nav-beneficiaries">{{ 'nav.beneficiaries' | transloco }}</a>
                  <a routerLink="/payments/managed-destinations" routerLinkActive="on">{{ 'nav.managedDestinations' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.APPROVE_PAYMENT)) {
                  <a routerLink="/approvals" routerLinkActive="on" data-testid="nav-approvals">{{ 'nav.approvals' | transloco }}</a>
                  <a routerLink="/payments/order-approval" routerLinkActive="on" data-testid="nav-order-approval">{{ 'nav.orderApprovals' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.INITIATE_PAYMENT) || capabilities.can(CODES.APPROVE_PAYMENT)) {
                  <a routerLink="/payments/rfp" routerLinkActive="on" data-testid="nav-rfp">{{ 'nav.rfp' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.PP_DECIDE_EXCEPTION)) {
                  <a routerLink="/positive-pay" routerLinkActive="on">{{ 'nav.positivePay' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.PP_CONFIGURE)) {
                  <a routerLink="/ach-debit-block" routerLinkActive="on">{{ 'nav.achDebitBlock' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.PP_DECIDE_EXCEPTION)) {
                  <a routerLink="/anomaly-alerts" routerLinkActive="on">{{ 'nav.anomalyAlerts' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.INITIATE_PAYMENT)) {
                  <a routerLink="/activity/payment-tools" routerLinkActive="on">{{ 'nav.paymentTools' | transloco }}</a>
                  <a routerLink="/payments/app-claims" routerLinkActive="on" data-testid="nav-app-claims">{{ 'nav.appClaims' | transloco }}</a>
                  }
                </div>
                }
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.activityInsights' | transloco }}</span>
                  <a routerLink="/activity" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }">{{ 'nav.activity' | transloco }}</a>
                  <a routerLink="/activity-report" routerLinkActive="on">{{ 'nav.allActivity' | transloco }}</a>
                  @if (capabilities.can(CODES.VIEW_ACCOUNT)) {
                  <a routerLink="/reporting" routerLinkActive="on">{{ 'nav.reporting' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.VIEW_REPORTS)) {
                  <a routerLink="/reports" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }">{{ 'nav.reports' | transloco }}</a>
                  <a routerLink="/reports/deliveries" routerLinkActive="on">{{ 'nav.reportDeliveries' | transloco }}</a>
                  @if (!capabilityAvailability.isUnavailable(REPORT_DISTRIBUTION_CAPABILITY_KEY)) {
                  <a routerLink="/reports/distribution" routerLinkActive="on" data-testid="nav-report-distribution">{{ 'nav.reportDistribution' | transloco }}</a>
                  }
                  <a routerLink="/reports/analysis-statement" routerLinkActive="on">{{ 'nav.accountAnalysis' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.FINANCIAL_MESSAGE_VIEW)) {
                  <a routerLink="/financial-messages" routerLinkActive="on">{{ 'nav.financialMessages' | transloco }}</a>
                  }
                  <a routerLink="/insights" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" data-testid="nav-insights">{{ 'nav.insights' | transloco }}</a>
                  @if (!capabilityAvailability.isUnavailable(INSIGHTS_ASK_CAPABILITY_KEY)) {
                  <a routerLink="/insights/ask" routerLinkActive="on" data-testid="nav-ask-insights">{{ 'nav.askInsights' | transloco }}</a>
                  }
                  <a routerLink="/notifications" routerLinkActive="on" data-testid="nav-notifications">{{ 'nav.notifications' | transloco }}</a>
                </div>
                @if (capabilities.can(CODES.RECON_RULE_MANAGE)) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.collectionsAr' | transloco }}</span>
                  <a routerLink="/receivables" routerLinkActive="on">{{ 'nav.receivables' | transloco }}</a>
                </div>
                }
                @if (capabilities.can(CODES.LC_OPEN) || capabilities.can(CODES.FX_DEAL_INITIATE) || capabilities.can(CODES.SWEEP_EXECUTE) || capabilities.can(CODES.SWEEP_CONFIGURE)) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.treasuryTrade' | transloco }}</span>
                  @if (capabilities.can(CODES.LC_OPEN)) {
                  <a routerLink="/trade" routerLinkActive="on">{{ 'nav.tradeFinance' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.FX_DEAL_INITIATE)) {
                  <a routerLink="/fx" routerLinkActive="on">{{ 'nav.fxDealing' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.SWEEP_EXECUTE)) {
                  <a routerLink="/liquidity" routerLinkActive="on">{{ 'nav.liquidity' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.SWEEP_CONFIGURE)) {
                  <a routerLink="/treasury-services" routerLinkActive="on">{{ 'nav.treasuryServices' | transloco }}</a>
                  <a routerLink="/customer-policy" routerLinkActive="on">{{ 'nav.customerPolicy' | transloco }}</a>
                  }
                </div>
                }
                @if (capabilities.can(CODES.CARD_ISSUE) || capabilities.can(CODES.CARD_SET_CONTROLS) || capabilities.can(CODES.FACILITY_APPLY) || capabilities.can(CODES.DEPOSIT_OPEN) || capabilities.can(CODES.MERCHANT_BOARD)) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.products' | transloco }}</span>
                  @if (capabilities.can(CODES.CARD_ISSUE)) {
                  <a routerLink="/cards" routerLinkActive="on">{{ 'nav.cards' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.CARD_SET_CONTROLS)) {
                  <a routerLink="/admin/cards/mcc-mappings" routerLinkActive="on">{{ 'nav.mccMappings' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.FACILITY_APPLY)) {
                  <a routerLink="/lending" routerLinkActive="on">{{ 'nav.lending' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.DEPOSIT_OPEN)) {
                  <a routerLink="/deposits" routerLinkActive="on">{{ 'nav.deposits' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.MERCHANT_BOARD)) {
                  <a routerLink="/merchant" routerLinkActive="on">{{ 'nav.merchant' | transloco }}</a>
                  }
                </div>
                }
                @if (capabilities.can(CODES.API_CLIENT_MANAGE)) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.integrations' | transloco }}</span>
                  <a routerLink="/integrations" routerLinkActive="on">{{ 'nav.erpConnections' | transloco }}</a>
                </div>
                }
                @if (capabilities.can(CODES.REPORT_DEFINE)) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.operations' | transloco }}</span>
                  <a routerLink="/admin/billing" routerLinkActive="on">{{ 'nav.feeOps' | transloco }}</a>
                </div>
                }
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.newBusiness' | transloco }}</span>
                  <a routerLink="/products" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" class="nav-cta">{{ 'nav.products' | transloco }}</a>
                  <a routerLink="/products/pre-approved" routerLinkActive="on">{{ 'nav.preApprovedOffers' | transloco }}</a>
                  <a routerLink="/onboarding" routerLinkActive="on" class="nav-cta">{{ 'nav.openAccount' | transloco }}</a>
                  <a routerLink="/financing" routerLinkActive="on" class="nav-cta">{{ 'nav.financing' | transloco }}</a>
                  <!-- F24: the route requires IMPLEMENTATION_MANAGE (ROUTE_ENTITLEMENT); the nav link was
                       missing this gate entirely, so a view-only persona saw the link then hit the
                       not-entitled panel. The rest of "New Business" stays universal (self-service
                       discovery, no route gate). -->
                  @if (capabilities.can(CODES.IMPLEMENTATION_MANAGE) && !capabilityAvailability.isUnavailable(IMPLEMENTATIONS_CAPABILITY_KEY)) {
                  <a routerLink="/implementations" routerLinkActive="on" data-testid="nav-implementations">{{ 'nav.implementations' | transloco }}</a>
                  }
                </div>
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.settings' | transloco }}</span>
                  <a routerLink="/settings/notifications" routerLinkActive="on">{{ 'nav.notifications' | transloco }}</a>
                  <a routerLink="/settings/display" routerLinkActive="on">{{ 'nav.displayPrivacy' | transloco }}</a>
                  <a routerLink="/security/factors" routerLinkActive="on" data-testid="nav-security">{{ 'nav.security' | transloco }}</a>
                </div>
                <!--
                  F15: the entitlements/authority surfaces split into two SCOPE-labelled sub-groups
                  so "Authority graph" (which legitimately appears once per scope — self vs admin,
                  same component, different starting context) is never an unbroken run of 14 flat,
                  unlabeled items. Labels below are kept 1:1 with entitlements-home.component.ts's
                  tile names (dailyOpsTiles/adminAuditTiles) so the hub and the rail teach the same
                  words. Every leaf mirrors entitlement-codes.ts ROUTE_ENTITLEMENT (or the route's own
                  data.entitlement override) so nav visibility never diverges from the not-entitled
                  in-place panel (persona-gate-outlet.component.ts).
                -->
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.yourAuthority' | transloco }}</span>
                  <a routerLink="/entitlements" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" data-testid="nav-my-authority">{{ 'nav.myAuthority' | transloco }}</a>
                  <a routerLink="/agent-authority" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" data-testid="nav-agent-authority">{{ 'nav.agentAuthority' | transloco }}</a>
                  <a routerLink="/authority" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" data-testid="nav-authority-graph-self">{{ 'nav.authorityGraph' | transloco }}</a>
                </div>
                @if (capabilities.can(CODES.USER_ADMIN) || capabilities.can(CODES.ENTITLEMENT_ADMIN) || capabilities.can(CODES.SECURITY_ADMIN) || capabilities.can(CODES.COMPANY_ADMIN) || capabilities.can(CODES.LIMIT_ADMIN) || capabilities.can(CODES.REPORT_DEFINE)) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.administerAuthority' | transloco }}</span>
                  @if (customerSurfaceAccess.canOpen('admin/users')) {
                  <a routerLink="/admin/users" routerLinkActive="on" data-testid="nav-users">{{ 'nav.users' | transloco }}</a>
                  }
                  <a routerLink="/admin/entitlements/home" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" data-testid="nav-entitlements-home">{{ 'nav.entitlementsHome' | transloco }}</a>
                  @if (capabilities.can(CODES.ENTITLEMENT_ADMIN)) {
                  <a routerLink="/admin/entitlements" routerLinkActive="on" [routerLinkActiveOptions]="{ exact: true }" data-testid="nav-effective-perms">{{ 'nav.effectivePerms' | transloco }}</a>
                  <a routerLink="/admin/entitlements/grant" routerLinkActive="on" data-testid="nav-grant-access">{{ 'nav.grantAccess' | transloco }}</a>
                  <a routerLink="/admin/entitlements/mutations" routerLinkActive="on" data-testid="nav-mutation-review">{{ 'nav.mutationReview' | transloco }}</a>
                  <a routerLink="/admin/entitlements/cutover" routerLinkActive="on" data-testid="nav-cutover">{{ 'nav.cutoverReadiness' | transloco }}</a>
                  <a routerLink="/admin/entitlements/approvers" routerLinkActive="on" data-testid="nav-approver-pool">{{ 'nav.approverPool' | transloco }}</a>
                  <a routerLink="/admin/entitlements/authority-graph" routerLinkActive="on" data-testid="nav-authority-graph-admin">{{ 'nav.authorityGraph' | transloco }}</a>
                  <a routerLink="/admin/entitlements/rewind" routerLinkActive="on" data-testid="nav-authority-rewind">{{ 'nav.authorityRewind' | transloco }}</a>
                  @if (customerSurfaceAccess.canOpen('admin/entitlements/change-log')) {
                  <a routerLink="/admin/entitlements/change-log" routerLinkActive="on" data-testid="nav-change-log">{{ 'nav.changeLog' | transloco }}</a>
                  }
                  <a routerLink="/admin/entitlements/subject-authority" routerLinkActive="on" data-testid="nav-subject-authority">{{ 'nav.subjectAuthority' | transloco }}</a>
                  <a routerLink="/admin/entitlements/step-up-policies" routerLinkActive="on" data-testid="nav-step-up-policies">{{ 'nav.stepUpPolicies' | transloco }}</a>
                  <!-- ADR-0089: manager recertification inbox -->
                  <a routerLink="/admin/entitlements/recert-inbox" routerLinkActive="on" data-testid="nav-recert-inbox">{{ 'nav.recertInbox' | transloco }}</a>
                  }
                  @if (customerSurfaceAccess.canOpen('admin/entitlements/limits')) {
                  <a routerLink="/admin/entitlements/limits" routerLinkActive="on" data-testid="nav-limit-changes">{{ 'nav.limitChanges' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.SECURITY_ADMIN)) {
                  @if (customerSurfaceAccess.canOpen('admin/reconciliation')) {
                  <a routerLink="/admin/reconciliation" routerLinkActive="on" data-testid="nav-reconciliation">{{ 'nav.reconciliation' | transloco }}</a>
                  }
                  <!-- ADR-0093: org security center -->
                  <a routerLink="/admin/security-center" routerLinkActive="on">{{ 'nav.securityCenter' | transloco }}</a>
                  }
                  @if (capabilities.can(CODES.REPORT_DEFINE)) {
                  <a routerLink="/admin/stress-lab" routerLinkActive="on" data-testid="nav-stress-lab">{{ 'nav.stressLab' | transloco }}</a>
                  }
                  @if (isCompanyAdmin() && capabilities.can(CODES.VIEW_AUTHENTICATOR_FLEET)) {
                  <a routerLink="/admin/authenticator-fleet" routerLinkActive="on">{{ 'nav.authenticatorFleet' | transloco }}</a>
                  }
                </div>
                }
                @if (isBankStaff()) {
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.administerAuthority' | transloco }}</span>
                  <a routerLink="/bank/authority-increases" routerLinkActive="on" data-testid="nav-bank-authority-increases">{{ 'nav.bankAuthorityIncreases' | transloco }}</a>
                </div>
                }
                <div class="nav-section">
                  <span class="nav-section-header" aria-hidden="true">{{ 'nav.support' | transloco }}</span>
                  <!-- ADR-0044 P1: help center -->
                  <a routerLink="/help" routerLinkActive="on" [attr.aria-label]="'nav.help' | transloco" data-testid="nav-help">
                    <span class="material-symbols-outlined" aria-hidden="true" style="font-size:16px;vertical-align:text-bottom">help</span>
                    {{ 'nav.help' | transloco }}
                  </a>
                  <!-- ADR-0029: customer-facing live co-session help entry point -->
                  <a routerLink="/help/live-assist" routerLinkActive="on" [attr.aria-label]="'nav.liveHelp' | transloco" data-testid="nav-live-help">
                    <span class="material-symbols-outlined" aria-hidden="true" style="font-size:16px;vertical-align:text-bottom">support_agent</span>
                    {{ 'nav.liveHelp' | transloco }}
                  </a>
                  <!-- ADR-0082: customer case centre (flag-gated; 503 shows in-page banner) -->
                  <a routerLink="/support/cases" routerLinkActive="on">{{ 'nav.myCases' | transloco }}</a>
                </div>
              </nav>
            </tb-mobile-nav>
            <span class="brand">CashBro</span>
          </div>
          <!--
            ADR-0106 demo/sim: mid-session "Switch Demo User" control. Centered between the
            CashBro wordmark (left) and the actions (right). Sized to the top-bar height — no
            vertical bleed, no overlap of the right-side content (docked in the top bar rather
            than a floating FAB in the debug wrapper). ENV-GATED (demo99): shown only on the DEMO env + its debug
            variant (demo./debug.demo.); HIDDEN on the sim env (sim./debug.sim.) where persona
            selection is by building-click portal (no switcher), and on the dev env
            (toybank./debug.toybank.) where user=persona 1:1 so a mid-session switch is moot.
            This @if also gates the active-persona label, so both vanish together on sim/dev.
            Backed by either the BFF persona switch (bff build) or the debug wrapper (debug build)
            via TB_PERSONA_SWITCHER — see personaSwitcher below.
          -->
          @if (showPersonaSwitch()) {
            <div class="bar-center">
              <!--
                F39: always-rendered, visually-hidden live region. The .active-persona-label span
                below is display:none under the same breakpoints that collapse the switcher
                trigger's text -- an aria-live host that can be hidden stops announcing the moment
                it is hidden, so a persona switch at a narrow bar width would go unannounced to
                AT. This node never collapses, so the announcement always fires regardless of
                chip visibility.
              -->
              <span class="sr-only" data-testid="persona-switch-live" aria-live="polite" aria-atomic="true">{{ 'Currently signed in as: ' + activePersonaLabel() }}</span>
              <!-- Currently-selected demo user label — fixed width, immediately left of the switcher (demo98) -->
              <span
                class="active-persona-label"
                data-testid="active-persona-label"
                aria-hidden="true"
              >{{ activePersonaLabel() }}</span>
              <!--
                Golden-v2 Lane C: two-pane "mall map" persona switcher.
                The trigger button is kept; the dropdown is now tb-demo-persona-map
                (left: persona list with summary sub-text + YOU ARE HERE; right: org-chart).
              -->
              <div class="persona-switch" [class.open]="personaSwitchOpen()" data-testid="persona-switch">
                <button
                  class="persona-switch-trigger"
                  type="button"
                  data-testid="persona-switch-trigger"
                  aria-label="Switch demo user"
                  title="Switch demo user"
                  [attr.aria-expanded]="personaSwitchOpen()"
                  aria-haspopup="dialog"
                  (click)="personaSwitchOpen.set(!personaSwitchOpen())"
                >
                  <!-- F39: compact persistent "viewing as" indicator -- survives the responsive
                       collapse that drops .persona-switch-label / .active-persona-label below,
                       so the bar never goes to zero indication of who is being acted as. -->
                  <span class="persona-switch-avatar" aria-hidden="true">{{ activePersonaInitial() }}</span>
                  <span class="material-symbols-outlined persona-switch-icon" aria-hidden="true">switch_account</span>
                  <span class="persona-switch-label">Switch Demo User</span>
                  <span class="persona-switch-firstname">{{ activePersonaFirstName() }}</span>
                  <span class="material-symbols-outlined persona-switch-caret" aria-hidden="true">expand_more</span>
                </button>
                @if (personaSwitchOpen()) {
                  <div class="persona-switch-map-panel" data-testid="persona-switch-map-panel">
                    <!-- F17: the panel closes itself on persona selection (or its own close
                         button); this is the one line that wires that back into the trigger's
                         open/close state, which stays owned here. -->
                    @defer (when personaSwitchOpen()) {
                      <tb-demo-persona-map (closed)="personaSwitchOpen.set(false)" />
                    }
                  </div>
                }
              </div>
            </div>
          }
          <div class="actions">
            <!--
              ADR-0039 AI-2a: command-first assistant launcher (global shell entry point).
              Keyboard: Enter/Space activates the inline command panel without leaving the page.
              Track 5 leg 2: Ctrl+K / Cmd+K toggles it globally (see handleAssistantHotkey);
              the kbd chip + title teach the shortcut.
            -->
            <button
              class="assist-launcher"
              type="button"
              [attr.aria-label]="'actions.openAssistant' | transloco"
              [attr.title]="'actions.assistantHotkeyTitle' | transloco"
              data-testid="assist-launcher-btn"
              (click)="openAssistant()"
              [attr.aria-expanded]="assistantOpen()"
            >
              <span class="material-symbols-outlined assist-launcher-icon" aria-hidden="true">robot_2</span>
              <span class="assist-launcher-label">{{ 'actions.assistant' | transloco }}</span>
              <kbd class="assist-hotkey-hint" aria-hidden="true" data-testid="assist-hotkey-hint">Ctrl K</kbd>
            </button>
            <button
              class="assist-history-launcher icon-btn"
              type="button"
              aria-label="Open assistant history"
              data-testid="assist-history-launcher-btn"
              (click)="openAssistHistory()"
              [attr.aria-expanded]="assistHistoryOpen()"
            >
              <span class="material-symbols-outlined" aria-hidden="true">history</span>
            </button>
            <!-- Language selector (navbar-language-selector) -->
            <div class="lang-selector" [class.open]="langMenuOpen()" data-testid="lang-selector">
              <button
                class="icon-btn lang-selector-trigger"
                type="button"
                [attr.aria-label]="staffLanguagePreview() ? ('languagePreview.select' | transloco) : 'Select language'"
                data-testid="lang-selector-btn"
                [attr.aria-expanded]="langMenuOpen()"
                [attr.aria-controls]="langMenuOpen() ? 'lang-menu' : null"
                aria-haspopup="menu"
                (click)="toggleLangMenu($event)"
              >
                <img
                  [src]="langFlagSrc[languageService.current()]"
                  [alt]="langDisplayName[languageService.current()]"
                  class="lang-flag"
                  aria-hidden="true"
                  width="20"
                  height="14"
                />
                @if (staffLanguagePreview() && languageService.previewSegment(); as segment) {
                  <span class="lang-preview-label" data-testid="lang-preview-label">{{ 'languagePreview.segments.' + segment | transloco }}</span>
                }
              </button>
              @if (langMenuOpen()) {
                <ng-container #languageMenuHost />
              }
            </div>
            <!-- Density toggle (dark/dense wave). tbTooltip mirrors aria-label exactly (owner
                 UX critique, 2026-09-14: icon-only buttons need a visible hover hint, not just an
                 accessible name) -- never a second, drifting copy of the label text. -->
            <button
              class="icon-btn"
              type="button"
              [attr.aria-label]="densityService.isCompact() ? ('actions.densityComfortable' | transloco) : ('actions.densityCompact' | transloco)"
              [tbTooltip]="densityService.isCompact() ? ('actions.densityComfortable' | transloco) : ('actions.densityCompact' | transloco)"
              data-testid="density-toggle"
              (click)="densityService.toggle()"
            >
              <span class="material-symbols-outlined" aria-hidden="true">{{ densityService.isCompact() ? 'density_small' : 'density_medium' }}</span>
            </button>
            <!-- Theme toggle (dark/dense wave) -->
            <button
              class="icon-btn"
              type="button"
              [attr.aria-label]="themeLabel()"
              [tbTooltip]="themeLabel()"
              data-testid="theme-toggle"
              (click)="themeService.toggle()"
            >
              <span class="material-symbols-outlined" aria-hidden="true">{{ themeIcon() }}</span>
            </button>
            <button type="button" class="icon-btn" title="Show or hide learning and access details"
              aria-label="Show or hide learning and access details" data-testid="overlay-toggle"
              [attr.aria-pressed]="overlaysEnabled()" (click)="toggleOverlays()">
              <span class="material-symbols-outlined" aria-hidden="true">info</span>
            </button>
            <!--
              Live platform insights is a BANK-STAFF layer (the Tiny World operator feed), not a
              customer affordance: a commercial-banking customer — and every demo persona — has no
              use for "how many orgs used this feature bank-wide" and reads it as leaked internals.
              Chrome-only gate (see isBankStaff): the layer itself is forced off for a non-staff
              identity by the effect next to that signal, so a stale localStorage preference cannot
              leave the overlay on with no way to turn it back off.
            -->
            @if (isBankStaff()) {
              @defer (on immediate) { <tb-insights-toggle title="Toggle AI insights" /> }
            }
            <!--
              Owner UX critique (2026-09-14): the debug wrapper's paired drawers used to toggle
              from their OWN header bar above the portal (a fixed 44px strip that existed only to
              hold one button). That bar is gone; the SAME toggle is now this manage_accounts
              icon, immediately left of the Org dropdown -- present only inside the debug build
              (see debugDrawers / TB_DEBUG_DRAWERS). class="drawer-handle" is a deliberate reuse:
              DebugShellComponent's Alt+Shift+D shortcut and its Escape-refocus logic both locate
              the toggle by that class name via a plain DOM querySelector, which still finds this
              button because Angular renders a child component's template as ordinary light-DOM
              children of its host element (no shadow DOM) -- so the nested <tb-root> button is a
              descendant of DebugShellComponent's own element just like the old bar's button was.
            -->
            @if (debugDrawers) {
              <button
                class="icon-btn drawer-handle"
                type="button"
                aria-label="Toggle Debug Login Wrapper"
                [tbTooltip]="'Toggle Debug Login Wrapper'"
                aria-controls="debug-drawer-left debug-drawer-right"
                aria-keyshortcuts="Alt+Shift+D"
                [attr.aria-expanded]="debugDrawers.drawersOpen()"
                data-testid="debug-drawers-toggle"
                (click)="debugDrawers.toggle()"
              >
                <span class="material-symbols-outlined" aria-hidden="true">manage_accounts</span>
              </button>
            }
            <span class="org-wrap">
              @if (townSession()) {
                <!-- Town authority remains eager, opaque and locked to the bound business. -->
                <select class="org" disabled [attr.aria-label]="'actions.orgId' | transloco" data-testid="org-id">
                  <option [value]="store.activeOrgId()">{{ townSelection()?.entityLabel || ('actions.orgUnavailable' | transloco) }}</option>
                </select>
              } @else {
              @defer (on immediate) {
                <tb-shell-org-selector [activeOrgId]="store.activeOrgId()" [options]="shellOrgOptions()"
                  [loading]="store.orgsLoading()" [error]="!!store.orgsError()"
                  [label]="'actions.orgId' | transloco" [loadingLabel]="'actions.orgLoading' | transloco"
                  [unavailableLabel]="'actions.orgUnavailable' | transloco"
                  [emptyLabel]="'actions.noOrg' | transloco" (changed)="setOrg($event)" />
              } @placeholder {
                <span class="org" aria-busy="true">{{ shellOrgSelectedLabel() }}</span>
              }
              }
            </span>
            @defer (on immediate) { <tb-notification-bell /> }
            @if (store.user()!.debugSession === true) {
              <span class="debug-session-badge" data-testid="debug-session-badge">
                {{ 'actions.debugSession' | transloco }}
              </span>
            }
            <span class="user">{{ store.user()!.name }}</span>
            <!--
              Logout: POST to /logout invalidates the Spring Session + OIDC RP-initiated logout.
              Uses a form POST (not a GET) so CSRF protection applies (the BFF expects POST for /logout).
              The BFF redirects to the Keycloak end_session endpoint, then back to /signed-out.
            -->
            <button
              class="logout-btn icon-btn"
              type="button"
              aria-label="Sign out"
              [tbTooltip]="'Sign out'"
              data-testid="logout-btn"
              [disabled]="loggingOut()"
              (click)="logout()"
            >
              <span class="material-symbols-outlined" aria-hidden="true">logout</span>
            </button>
          </div>
        </header>

        <!-- Toast tray — above all content -->
        <tb-notification-toast />

        <section class="banner-stack" aria-label="Security notices">
          <!--
            Platform advisories v1 — top of the banner stack, above every feature banner below.
            CRITICAL advisories show on every route; WARN/INFO are scoped to the current route's
            backing services (route-services.ts). currentUrl() is the SAME router-derived signal
            the factor-enrollment gate and public-route check already use.
          -->
          <tb-advisory-banner [services]="currentRouteServices()" />

          <!-- Townsim provenance strip — pins the simulated business identity to a TOWNSIM_ORG session -->
          <tb-townsim-provenance-banner [contextKey]="townContextKey()"
            [memberHandle]="store.user()?.memberHandle ?? ''"
            (selectionChanged)="townProjection.set($event)" />

          <!-- Cosession active-session banner (sticky under top bar when session is live) -->
          @if (!townSession()) {
            @defer (on immediate) { <tb-cosession-active-banner /> }
          }

          <!-- Factor expiry banner — shows when any factor is EXPIRING_SOON or EXPIRED -->
          <tb-factor-expiry-banner />

          <!-- Recovery mode banner — shows when a constrained recovery session is active (ADR-0031) -->
          <tb-recovery-mode-banner />

          <!-- Logout-failure banner: a failed server-side logout must never look successful --
               do not navigate on error; surface it here with a retry action instead. -->
          @if (logoutError()) {
            <div class="logout-error-banner" role="alert" aria-live="assertive" data-testid="logout-error-banner">
              <span class="banner-icon material-symbols-outlined" aria-hidden="true">error</span>
              <span class="banner-text">{{ 'actions.logoutError' | transloco }}</span>
              <button type="button" class="banner-action" data-testid="logout-retry-btn"
                [disabled]="loggingOut()" (click)="logout()">{{ 'actions.logoutRetry' | transloco }}</button>
              <button type="button" class="banner-dismiss" data-testid="logout-error-dismiss"
                [attr.aria-label]="'actions.logoutErrorDismiss' | transloco" (click)="logoutError.set(false)">
                <span class="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </div>
          }
        </section>

        @defer (when assistantOpen()) {
        @if (assistantOpen()) {
          <!--
            Track 5 leg 2 (a11y): Escape closes the panel when focus is WITHIN it — the handler
            sits on the section so it only fires for keydowns bubbling from inside. This
            COMPOSES with the document-level persona-switcher Escape handler (which never has
            focus inside this panel when its dropdown is open) instead of replacing it.
          -->
          <section
            class="assist-panel"
            aria-label="Assistant"
            data-testid="assist-inline-panel"
            (keydown.escape)="closeAssistant()"
          >
            <div class="assist-panel-head">
              <span class="material-symbols-outlined assist-panel-robot" aria-hidden="true">robot_2</span>
              <h2>{{ 'actions.assistant' | transloco }}</h2>
              <!--
                Set-up tour toggle. The tour lives HERE, beside the assistant, rather than only on
                /home: talking to the assistant ABOUT a tour step is a first-class use case, and a
                checklist you have to navigate back to /home to re-read is a checklist nobody
                re-reads. Rendered only for a session that actually has a tour (see
                tutorialAvailable), so an ordinary session's panel head is unchanged.
              -->
              @if (tutorialAvailable()) {
                <button
                  type="button"
                  class="assist-tour-toggle"
                  [class.on]="tutorialPaneOpen()"
                  [attr.aria-pressed]="tutorialPaneOpen()"
                  [attr.aria-label]="tutorialPaneOpen() ? 'Hide the set-up tour' : 'Show the set-up tour'"
                  (click)="toggleTutorialPane()"
                  data-testid="assist-tour-toggle"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">flag</span>
                  <span class="assist-tour-toggle-label">Set-up tour</span>
                </button>
              }
              <button class="icon-btn assist-close" type="button" (click)="closeAssistant()" aria-label="Close assistant">
                <span class="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </div>

            <!--
              Two columns: the conversation on the left, the set-up tour docked on the right. The
              tour column is a real sibling (not an overlay) so both scroll independently and
              neither can cover the other. Below the panel's own breakpoint they stack, tour
              first — on a phone the tour is why the panel was opened.
            -->
            <div class="assist-panel-cols" [class.with-tour]="tutorialPaneOpen()">
            <div class="assist-panel-body">

            <tb-command-box #shellCommandBox
              [autofocusOnInit]="true"
              [compact]="true"
              (submitted)="onShellSubmitted($event)"
              (resolved)="onShellResolved($event)"
              (failed)="onShellResolveFailed($event)"
              data-testid="shell-assist-command-box"
            />

            <!--
              Track 5 leg 2 (a11y): ONE polite live region wraps the whole results stack so a
              screen reader announces whichever card a resolve produces. The command box's own
              sr-only region announces the OTHER event class (loading/error status) and errors
              are role="alert" — so each event class has exactly one announcer. The result cards
              inside must NOT carry their own role="status" (double-announce).
            -->
            <div class="assist-results" aria-live="polite" data-testid="shell-assist-results">

            <!--
              Session-log thread: honest THINKING state — the resolve round-trip is a real
              wait (local-model parse can take up to ~20s). Elapsed seconds + a truthful
              slow hint; no fake streaming, no fabricated progress (the backend is strictly
              request/response). Lives inside the single polite region so the state change
              announces once.
            -->
            @if (assistThread.thinkingTurn(); as thinkingTurn) {
              <div class="assist-thinking" data-testid="assist-thinking">
                <span class="thinking-dots" aria-hidden="true"><span></span><span></span><span></span></span>
                <div class="assist-thinking-copy">
                  <p class="assist-thinking-title">
                    Working on it<span class="thinking-elapsed"> — {{ assistThread.thinkingSeconds() }}s</span>
                  </p>
                  <p class="assist-thinking-cmd"><code>{{ displayAssistUserCommand(thinkingTurn.command) }}</code></p>
                  @if (assistThread.thinkingSeconds() >= THINKING_SLOW_HINT_SECONDS) {
                    <p class="assist-thinking-hint" data-testid="assist-thinking-slow-hint">
                      Still thinking — the local model can take up to 20 seconds. Your command has not been lost.
                    </p>
                  }
                </div>
              </div>
            }

            @if (shellDeniedResponse(); as denied) {
              <div class="assist-denied" data-testid="shell-assist-not-entitled">
                <span class="material-symbols-outlined" aria-hidden="true">block</span>
                <div class="assist-denied-copy">
                  <p class="assist-denied-title">Action blocked</p>
                  <p class="assist-denied-body">
                    You are not entitled to perform this action: {{ actionLabel(denied.actionKind) }}.
                  </p>
                  <p class="assist-denied-reason" data-testid="shell-assist-denial-reason">
                    <span>{{ shellDeniedGateKind(denied) || 'Authorization' }} denied:</span>
                    {{ shellDeniedReason(denied) }}
                  </p>
                  <!--
                    Denial follow-ups (mirrors the intent modal's who-can / explain affordance):
                    honest next steps AS THE USER — both are pure reads via /api/assist/query;
                    the engine answers, the SPA never computes authority.
                  -->
                  <div class="assist-denied-actions">
                    <button
                      type="button"
                      class="assist-denied-followup"
                      (click)="launchShellWhoCan(denied)"
                      [disabled]="shellDeniedQueryLoading() !== null"
                      data-testid="assist-denied-whocan-btn"
                    >
                      <span class="material-symbols-outlined" aria-hidden="true">groups</span>
                      Who can do this?
                    </button>
                    <button
                      type="button"
                      class="assist-denied-followup"
                      (click)="launchShellExplainDenied(denied)"
                      [disabled]="shellDeniedQueryLoading() !== null"
                      data-testid="assist-denied-why-btn"
                    >
                      <span class="material-symbols-outlined" aria-hidden="true">policy</span>
                      Why exactly?
                    </button>
                  </div>
                </div>
              </div>
              @if (shellDeniedQueryLoading(); as queryKind) {
                <p class="assist-denied-query-status" data-testid="assist-denied-query-loading">
                  {{ queryKind === 'WHO_CAN_QUERY' ? 'Asking the engine who can…' : 'Asking the engine why…' }}
                </p>
              }
              @if (shellDeniedQueryError(); as queryError) {
                <p class="assist-denied-query-status assist-denied-query-error" data-testid="assist-denied-query-error">{{ queryError }}</p>
              }
              @if (shellDeniedQueryResult()) {
                <tb-assist-query-panel [response]="shellDeniedQueryResult()" data-testid="assist-denied-query-panel" />
              }
            }

            @if (shellEntitlementQueryResponse()) {
              <tb-assist-entitlement-query-panel
                [response]="shellEntitlementQueryResponse()"
                (completed)="onShellEntitlementQueryCompleted($event)"
                (failed)="onShellEntitlementQueryFailed($event)"
                data-testid="shell-entitlement-query-auto-panel"
              />
            }

            @if (shellRmReadResponse()) {
              <tb-assist-rm-read-panel
                [response]="shellRmReadResponse()"
                data-testid="shell-rm-read-panel"
              />
            }

            @if (shellAccountReadResponse()) {
              <tb-assist-account-read-panel
                [response]="shellAccountReadResponse()"
                data-testid="shell-account-read-panel"
              />
            }

            @if (shellInsightsResponse()) {
              <tb-assist-insights-panel
                [response]="shellInsightsResponse()"
                (dismissed)="dismissShellInsights()"
                data-testid="shell-assist-insights-panel"
              />
            }

            @if (shellHelpResponse()) {
              <div class="assist-help-card" role="region" aria-label="Help article" data-testid="shell-assist-help-card">
                <div class="assist-help-head">
                  <span class="material-symbols-outlined" aria-hidden="true">help</span>
                  <div class="assist-help-copy">
                    <p class="assist-help-label">Help</p>
                    <p class="assist-help-title">{{ safeAssistantText(shellHelpResponse()!.helpArticle!.title, shellHelpResponse()) }}</p>
                  </div>
                </div>
                <p class="assist-help-summary">{{ safeAssistantText(shellHelpResponse()!.helpArticle!.summary, shellHelpResponse()) }}</p>
                <div class="assist-help-actions">
                  <button type="button" class="assist-open-help" (click)="openHelpArticle(shellHelpResponse()!.helpArticle!)">
                    <span class="material-symbols-outlined" aria-hidden="true">open_in_new</span>
                    Open in Help
                  </button>
                  <button type="button" class="assist-dismiss" (click)="dismissShellHelp()" aria-label="Dismiss help article">
                    <span class="material-symbols-outlined" aria-hidden="true">close</span>
                  </button>
                </div>
              </div>
            }

            <!--
              ADR-0044 GUIDE route: UI-navigation guidance -- calm/read-only treatment.
              Shown when the server classified the command as a "where is X / how do I get to Y"
              question. Steps are corpus-sourced, never LLM-invented. "Take me there" uses the
              SPA router -- no execute path, no bearer forwarding, no money movement.
            -->
            @if (shellGuideResponse(); as guideResp) {
              <div class="assist-guide-card" role="region" aria-label="Navigation guide" data-testid="shell-assist-guide-card">
                <div class="assist-guide-head">
                  <span class="material-symbols-outlined" aria-hidden="true">explore</span>
                  <div class="assist-guide-copy">
                    <p class="assist-guide-label">How to get there</p>
                    <p class="assist-guide-title">{{ safeAssistantText(guideResp.guide!.title, guideResp) }}</p>
                    @if (guideResp.guide!.navBreadcrumb) {
                      <p class="assist-guide-breadcrumb" data-testid="shell-assist-guide-breadcrumb">{{ safeAssistantText(guideResp.guide!.navBreadcrumb, guideResp) }}</p>
                    }
                  </div>
                  <button
                    type="button"
                    class="assist-dismiss"
                    (click)="dismissShellGuide()"
                    aria-label="Dismiss navigation guide"
                    data-testid="shell-assist-guide-dismiss"
                  >
                    <span class="material-symbols-outlined" aria-hidden="true">close</span>
                  </button>
                </div>
                <ol class="assist-guide-steps" data-testid="shell-assist-guide-steps">
                  @for (step of guideResp.guide!.steps; track $index) {
                    <li class="assist-guide-step">{{ safeAssistantText(step, guideResp) }}</li>
                  }
                </ol>
                <div class="assist-guide-actions">
                  <button
                    type="button"
                    class="assist-guide-go-btn"
                    (click)="navigateGuide(guideResp.guide!)"
                    data-testid="shell-assist-guide-go-btn"
                  >
                    <span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
                    Take me there
                  </button>
                </div>
              </div>
            }

            @if (shellClarifyWithSuggestions()) {
              <div class="assist-suggestions" role="region" aria-label="Suggested help articles" data-testid="shell-assist-suggestions">
                <div class="assist-suggestion-list">
                  @for (article of shellClarifyWithSuggestions()!.suggestedArticles ?? []; track article.id) {
                    <button
                      class="assist-suggestion"
                      type="button"
                      (click)="openHelpArticle(article)"
                      [attr.aria-label]="'Open help: ' + safeAssistantText(article.title, shellClarifyWithSuggestions())"
                    >
                      <span class="material-symbols-outlined" aria-hidden="true">help</span>
                      {{ safeAssistantText(article.title, shellClarifyWithSuggestions()) }}
                    </button>
                  }
                </div>
              </div>
            }

            @if (shellLastDecision()) {
              @if (shellLastDecision()!.choice === 'INSTANT') {
                <div class="assist-authorized" data-testid="shell-assist-authorized">
                  <span class="material-symbols-outlined" aria-hidden="true">task_alt</span>
                  <div>
                    <p class="assist-authorized-title">Authorized and ready</p>
                    <p class="assist-authorized-copy">Final execution is disabled in this demo environment.</p>
                  </div>
                </div>
              } @else if (shellLastDecision()!.choice === 'CANCELLED') {
                <div class="assist-decision" data-testid="shell-assist-last-decision">
                  <span>Last command:</span>
                  <code>{{ shellLastDecision()!.response.actionKind }}</code>
                  <span>Cancelled</span>
                </div>
              }
            }
            </div><!-- /.assist-results (single polite live region) -->

            <!--
              The session log (ADR-0039 D7): durable, read-only record of this session's
              command turns — prepare-not-execute made visible as history. Static DOM,
              deliberately OUTSIDE the live region (only the latest state change announces).
            -->
            <tb-assist-thread
              [turns]="assistThread.completedTurns()"
              [suppressEmptyState]="true"
              (rerun)="rerunAssistCommand($event)"
            />
            </div><!-- /.assist-panel-body -->

            @if (tutorialPaneOpen()) {
              <aside
                class="assist-tour-pane"
                aria-label="Set-up tour"
                data-testid="assist-tour-pane"
              >
                <!-- A standing label so the column is never an unexplained blank: the checklist
                     below renders nothing at all when the quest flag is off server-side (its
                     deliberate 404 contract), and an empty unlabelled column reads as a bug. -->
                <p class="assist-tour-pane-label">Set-up tour</p>
                <tb-guided-onboarding-checklist />
              </aside>
            }
            </div><!-- /.assist-panel-cols -->
          </section>
        }
        }

        <!-- ── Body: rail + content ── -->
        <div class="body">
          <!-- Cosession consent banner (inside content area, above router outlet) -->
          <!-- F38: skip-link target -- tabindex="-1" makes an otherwise non-interactive container
               programmatically focusable so activating .skip-link above actually moves focus here
               (a plain #id anchor jump alone does not move keyboard focus in most browsers). -->
          <div class="content-wrap" id="main-content" tabindex="-1">
            @if (!townSession()) {
            @defer (when pendingCosessionConsent() || consentAttention.pendingAttention(); prefetch on immediate) {
              <tb-cosession-consent-banner (ready)="consentBannerReady.set(true)" />
            }
            }
            <!--
              PBAC+ReBAC Phase 3: the authenticated outlet is wrapped so a DEMO persona that lacks the
              active route's entitlement sees an in-place "Not entitled" panel (no redirect). Real
              users always get the outlet (server 403 stays the safety net). The PRE-AUTH/public
              outlet above (public-shell) stays a plain <router-outlet /> — gating is post-auth only.
            -->
            <tb-persona-gate-outlet />
          </div>
        </div>

        <!-- Cosession: remote cursor overlay (fixed, pointer-events:none) -->
        @if (!townSession()) {
        @defer (on immediate) { <tb-cosession-remote-cursor /> }

        <!-- Cosession: action approval modal (blocking) -->
        @defer (on immediate) { <tb-cosession-approval-modal /> }

        <!-- Cosession: nav-follow toast -->
        @defer (on immediate) { <tb-cosession-nav-toast /> }
        }

        <!-- ADR-0035: step-up re-auth ceremony dialog (fixed overlay, rendered when a challenge is pending) -->
        @defer (when stepUpCeremony.pendingCeremony() !== null; prefetch on immediate) {
          <tb-step-up-dialog />
        }

        @defer (on immediate) { <tb-guide-highlight /> }

        <!-- ADR-0180 D4: the chatbot SPOTLIGHT layer (highlight/arrow). Distinct from
             tb-guide-highlight above (the ADR-0039/0044 "show me how" tour) — a sibling
             overlay, not a replacement. Non-blocking; suspends while any modal is open. -->
        @defer (on immediate) { <tb-spotlight-host /> }

        <!-- Public-demo first-run landing (owner brief 2026-09-12): step 1 asks who the visitor
             wants to be (the SAME tb-demo-persona-map, in landing mode), step 2 spotlights the
             top-bar trigger via the layer above. Inert everywhere the demo persona layer does not
             apply, and it renders nothing at all until DemoPersonaLandingService says a step is
             open — this sits inside the authenticated shell, so the demo bootstrap gate has
             already resolved by the time it can mount. -->
        @defer (on immediate) { <tb-demo-persona-landing /> }

        <!-- EMPHASIS layer (assist/emphasis.service.ts): the standing amber pulse the set-up tour
             puts on the control it is asking for, plus the bouncing edge arrow that appears when
             that control is scrolled out of sight. Third sibling of the two overlays above and
             deliberately not merged with either — see assist/emphasis.model.ts. -->
        @defer (on immediate) { <tb-emphasis-arrow /> }

        <!-- ADR-0036 S7: first-login factor enrollment is now a DEDICATED ROUTED SCREEN
             (/login/enroll-factor), not a modal. The shell redirects there via an effect
             when the gate is active (see redirectToEnrollmentGate); no overlay is rendered. -->


        <!--
          ADR-0039 AI-2a: global command intent modal.
          Rendered when the inline shell assistant resolves an ACTION response.
        -->
        @defer (when shellPendingResponse() !== null) {
        <tb-command-intent-modal
          [response]="shellPendingResponse()"
          (decided)="onShellDecided($event)"
          (showMeHow)="onShellShowMeHow($event)"
          (rephrase)="onShellRephrase($event)"
          data-testid="shell-intent-modal"
        />
        }

        @defer (when assistHistoryOpen()) {
          <tb-assist-history-drawer
            [open]="assistHistoryOpen()"
            (closed)="closeAssistHistory()"
            (rerunRequested)="rerunAssistCommand($event)"
          />
        }

        <!--
          Inactivity idle-warning: mounted once inside the authenticated shell so it only tracks a
          signed-in user. Renders nothing until the user nears the server session idle deadline
          (Spring Session 30 min), then warns + offers Stay-signed-in / Sign-out-now; auto signs
          out at zero. Tokens stay server-side; sign-out posts to /logout, then shows /signed-out.
        -->
        <tb-idle-warning />
      </div>
    }
  `,
  styles: `
    /* ── Login surface: shared <tb-login-screen> owns its own styles (login-screen.component.ts).
       .center below is a generic centered/padded block, also used by the "Checking session…"
       message (sessionLoading branch) -- kept independent of the login screen's own layout. ── */

    /* The shared login screen has min-height 100%, which needs an ancestor with a real height.
       Here it is mounted straight onto <tb-root>, whose default display:inline / auto height
       collapses that 100% to the login card's own height -- so the hero panel rendered as a short
       strip with empty page beneath it. Give the host the viewport height the same way
       .public-shell above does. This is deliberately fixed HERE and not in the shared component:
       the debug wrapper (ADR-0105) mounts the same component inside an overflow:auto padded
       grid column that already has a bounded height, and forcing 100dvh there would add a
       scrollbar the width of that column's padding.

       Grid with an explicit 1fr row, not plain block: a percentage min-height on the child
       resolves against the parent's HEIGHT, which is still auto here, so block display leaves
       .login-screen collapsed even once the host itself is viewport-tall. A single 1fr row makes
       the child fill the host outright, no percentage involved. */
    tb-login-screen { display: grid; grid-template-rows: 1fr; min-height: 100dvh; }
    .center { max-width: 480px; margin: 0; padding: 4rem 2rem; display: flex; flex-direction: column; justify-content: center; }

    /* ── Public (pre-auth) shell: brand header + centered outlet, no portal chrome ── */
    .public-shell {
      min-height: 100dvh; display: flex; flex-direction: column;
      background: var(--tb-surface-bg, #0f1115);
    }
    .public-bar {
      display: flex; align-items: center; padding: 0.75rem 1.25rem;
      border-bottom: 1px solid var(--tb-border-line, #2a2f3a);
      background: var(--tb-surface-panel, #1a1d24);
    }
    .public-content {
      flex: 1; display: flex; flex-direction: column; align-items: center;
      width: 100%; max-width: 640px; margin: 0 auto;
      padding: 2.5rem 1.25rem; box-sizing: border-box;
    }

    /* ── Shell layout ── */
    .shell {
      /* Owner UX critique (2026-09-14): --tb-app-bar-height used to alias --tb-touch (48px
         comfortable / 36px compact), so the bar itself shrank in dense mode -- and the dense
         TOGGLE lives inside that same bar, so clicking it moved the bar (and the button under the
         pointer) out from under the cursor. The bar is chrome, not a touch target: pin it to a
         constant height regardless of density. Individual controls inside it (.icon-btn is a
         fixed 36x36px already; the persona-switch trigger below) are free to have their own
         density-aware sizing -- only the BAR's own height must never change. */
      --tb-app-bar-height: 48px;
      --tb-nav-rail-width: 212px;
      display: flex; flex-direction: column;
      height: var(--tb-app-viewport-height, 100dvh); min-height: 0; overflow: hidden;
      background: var(--tb-surface-bg, #0f1115);
    }

    .bar {
      display: flex; align-items: center; justify-content: space-between; gap: 1rem;
      padding: 0 0.75rem; height: var(--tb-app-bar-height, 48px);
      border-bottom: 1px solid var(--tb-border-line, #2a2f3a);
      background: var(--tb-surface-panel, #1a1d24);
      position: sticky; top: 0;
      /* z-sticky (200): the bar's stacking context must sit ABOVE the assist-panel flex sibling
         (z-index: 180 as a flex child of .shell). Without this the bar stacking context painted
         at z-index: 100, below the assist-panel, so the demo-switcher dropdown (.persona-switch-map-panel,
         z-200 within the bar's context) appeared BEHIND the open assistant box. Raising the bar
         to --tb-z-sticky ensures the whole bar stacking unit (including its absolutely-positioned
         descendants) clears the assist-panel. The command-intent-modal sits at --tb-z-modal (400)
         and is a sibling of the bar, so it still wins over the dropdown. */
      z-index: var(--tb-z-sticky, 200);
      /* Container query context so the centered switch can compact when the BAR itself is
         narrow — not just the viewport. This matters inside the debug wrapper, where the
         portal lives in a constrained center column (~830px) while the viewport is wide, so
         viewport media queries don't fire. */
      container-type: inline-size;
      container-name: tb-bar;
    }
    .bar-left { display: flex; align-items: center; gap: 0.5rem; }
    .brand { font-weight: 700; letter-spacing: -0.01em; font-size: 1.05rem; }

    /* ── Centered "Switch Demo User" region (demo/sim envs only) ──
       flex:1 fills the gap between the left (wordmark) and right (actions) groups;
       the control itself is centered within it and sized to the bar height (no bleed). */
    .bar-center {
      flex: 1 1 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      min-width: 0;
    }
    /* F39/F38: standard visually-hidden-but-announced utility (input still reachable by AT/keyboard,
       invisible on screen). Used by the always-rendered persona-switch-live region and the
       skip-to-main link (visible only on focus, see .skip-link below). */
    .sr-only {
      position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
      overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
    }
    /* F38: skip-to-main link -- off-screen until focused, then docked top-left above everything
       so a keyboard/AT user can see and activate it before tabbing through the top bar. */
    .skip-link {
      position: absolute; top: -3rem; left: 0.5rem;
      z-index: var(--tb-z-toast, 500);
      padding: 0.5rem 1rem; border-radius: var(--tb-radius-sm, 8px);
      background: var(--tb-surface-panel, #1a1d24);
      color: var(--tb-text-primary, #e6e9ef);
      border: 1px solid var(--tb-brand-accent, #4f86f7);
      font-size: 0.85rem; font-weight: 600; text-decoration: none;
      transition: top var(--tb-motion-fast, 120ms);
    }
    .skip-link:focus-visible {
      top: 0.5rem;
      outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 2px;
    }
    @media (prefers-reduced-motion: reduce) { .skip-link { transition: none; } }
    /* Keeps the persona-switch-live/other sr-only regions un-scrollable but focusable content
       (like .content-wrap below) from taking a visible focus ring in mouse-only interactions. */
    .content-wrap:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: -2px; }
    .content-wrap:focus:not(:focus-visible) { outline: none; }
    /* Currently-selected demo persona label — fixed width so bar layout is stable (demo98) */
    .active-persona-label {
      display: inline-block;
      min-width: 9rem; max-width: 9rem;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      text-align: right;
      font-size: 0.8rem; font-weight: 500;
      color: var(--tb-text-muted, #97a0b0);
      /* Subtle separator between label and the switcher button */
      padding-right: 0.4rem;
      border-right: 1px solid var(--tb-border-line, #2a2f3a);
      line-height: 1;
    }
    /* Hide label when the bar collapses the switcher label (same breakpoints) */
    @container tb-bar (max-width: 1100px) {
      .active-persona-label { display: none; }
    }
    @media (max-width: 720px) {
      .active-persona-label { display: none; }
    }
    .persona-switch { display: inline-flex; }
    .persona-switch-trigger {
      display: inline-flex; align-items: center; gap: 0.35rem;
      height: 32px; max-height: calc(var(--tb-touch, 48px) - 12px);
      padding: 0 0.7rem; box-sizing: border-box;
      border-radius: var(--tb-radius-pill, 999px);
      border: 1px solid color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 45%, var(--tb-border-line, #2a2f3a));
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 12%, transparent);
      color: var(--tb-text-primary, #e6e9ef);
      cursor: pointer; font-family: inherit; font-size: 0.82rem; font-weight: 600;
      white-space: nowrap; line-height: 1;
      transition: background var(--tb-motion-fast, 120ms), border-color var(--tb-motion-fast, 120ms);
    }
    .persona-switch-trigger:hover {
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 22%, transparent);
      border-color: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 70%, transparent);
    }
    .persona-switch-trigger:focus-visible {
      outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 2px;
    }
    @media (prefers-reduced-motion: reduce) { .persona-switch-trigger { transition: none; } }
    .persona-switch-icon {
      font-size: 1rem; flex-shrink: 0; color: var(--tb-accent-cosession-ai, #7C3AED);
    }
    .persona-switch-caret { font-size: 1rem; flex-shrink: 0; color: var(--tb-text-muted, #97a0b0); }
    /* F39: compact avatar-or-initial badge -- always rendered (never collapses), so the trigger
       itself always carries a "who is this" signal even at the narrowest bar widths. */
    .persona-switch-avatar {
      display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
      width: 1.15rem; height: 1.15rem; border-radius: 50%;
      background: var(--tb-accent-cosession-ai, #7C3AED); color: #fff;
      font-size: 0.62rem; font-weight: 700; line-height: 1;
    }
    /* F39: compact first-name companion -- hidden while the full ".persona-switch-label" text is
       visible (would be redundant); shown once the label collapses so a name (not just the switch
       icon) still reads at narrow bar widths. */
    .persona-switch-firstname {
      display: none;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 5rem;
    }
    .persona-switch-label {
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    /* Drop the label when the BAR is narrow (container query) so the control collapses to a
       compact icon + caret and still fits between the wordmark and the actions cluster. Using
       a container query (not a media query) so it fires inside the debug wrapper's narrow
       portal column, where the viewport is wide but the bar is not. A viewport fallback keeps
       the same behavior on genuinely small screens for browsers without container queries. */
    @container tb-bar (max-width: 1100px) {
      .persona-switch-label { display: none; }
      .persona-switch-firstname { display: inline-block; }
    }
    @media (max-width: 720px) {
      .persona-switch-label { display: none; }
      .persona-switch-firstname { display: inline-block; }
    }
    .persona-switch-menu {
      position: absolute; top: calc(100% + 0.4rem); left: 0; right: 0; margin-inline: auto;
      min-width: 16rem; max-width: min(22rem, 80vw);
      z-index: var(--tb-z-sticky, 200);
      display: flex; flex-direction: column; gap: 0.1rem; padding: 0.35rem;
      background: var(--tb-surface-panel, #1a1d24);
      border: 1px solid var(--tb-border-line, #2a2f3a);
      border-radius: var(--tb-radius-sm, 8px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.38);
    }
    .persona-switch-item {
      display: flex; align-items: center; gap: 0.5rem;
      width: 100%; min-height: 36px; padding: 0.35rem 0.55rem;
      border: 0; border-radius: var(--tb-radius-sm, 6px);
      background: transparent; color: var(--tb-text-primary, #e6e9ef);
      cursor: pointer; font: inherit; font-size: 0.84rem; text-align: left;
    }
    .persona-switch-item:hover:not(:disabled) { background: var(--tb-surface-inset, #11141a); }
    .persona-switch-item:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: -2px; }
    .persona-switch-item:disabled { cursor: progress; opacity: 0.8; }
    .persona-switch-item.active { color: var(--tb-accent-cosession-ai, #b794f6); font-weight: 650; }
    .persona-switch-item .material-symbols-outlined { font-size: 1rem; flex-shrink: 0; }
    .persona-switch-item-dot {
      width: 1rem; height: 1rem; flex-shrink: 0;
    }
    .persona-switch-item-label {
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .persona-switch-item .spin { color: var(--tb-accent-cosession-ai, #7C3AED); animation: tb-persona-spin 900ms linear infinite; }
    @keyframes tb-persona-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .persona-switch-item .spin { animation: none; } }

    /* ── Golden-v2 Lane C: two-pane map panel ── */
    .persona-switch-map-panel {
      position: absolute; top: calc(100% + 0.4rem); left: 0; right: 0; margin-inline: auto;
      width: min(72rem, 92cqw);
      z-index: var(--tb-z-sticky, 200);
      background: var(--tb-surface-panel, #1a1d24);
      border: 1px solid var(--tb-border-line, #2a2f3a);
      border-radius: var(--tb-radius-sm, 8px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.42);
      overflow: hidden;
    }
    @media (max-width: 640px) {
      .persona-switch-map-panel {
        left: 0; transform: none; right: 0; width: auto;
      }
    }

    .actions {
      display: flex; align-items: center; gap: 0.5rem;
      min-width: 0;
    }
    /* Collapse toggles at sm — they're secondary on mobile */
    @media (max-width: 600px) {
      .actions > tb-education-toggle,
      .actions > tb-insights-toggle,
      .actions > tb-entitlement-toggle,
      .actions > tb-explainer { display: none; }
      .org { width: 8rem; font-size: 0.75rem; }
    }

    .org-wrap { display: inline-flex; align-items: center; }
    .org {
      /* Widened (demo98): min-width ensures "Olympus Financial Group" fits untruncated */
      min-width: 16rem; width: 16rem; max-width: 34vw;
      padding: 0.35rem 0.6rem;
      background: var(--tb-surface-inset, #11141b); color: inherit;
      border: 1px solid var(--tb-border-line, #2a2f3a); border-radius: var(--tb-radius-sm, 6px);
      font-family: var(--tb-font-mono, ui-monospace, monospace);
      /* prevent iOS zoom on focus */
      font-size: max(16px, 0.85rem);
    }
    .org:disabled {
      opacity: 0.78;
      cursor: default;
    }
    /* Single-org hint option: italic, muted, non-interactive (demo98) */
    .org-single-hint {
      font-style: italic;
      color: var(--tb-text-muted, #97a0b0);
      border-top: 1px solid var(--tb-border-line, #2a2f3a);
    }
    .user { font-size: 0.9rem; color: var(--tb-text-muted, #9aa5b1); white-space: nowrap; }
    .debug-session-badge {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      padding: 0 0.5rem;
      border: 1px solid color-mix(in srgb, var(--tb-status-warning, #e0af68) 55%, var(--tb-border-line, #2a2f3a));
      border-radius: 999px;
      background: color-mix(in srgb, var(--tb-status-warning, #e0af68) 14%, var(--tb-surface-panel, #1a1d24));
      color: var(--tb-status-warning, #e0af68);
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.025em;
      white-space: nowrap;
    }

    /* ── Theme / density icon buttons (dark/dense wave) ── */
    .icon-btn {
      display: inline-flex; align-items: center; justify-content: center;
      flex-shrink: 0; position: relative; z-index: 1;
      width: 36px; height: 36px;
      background: transparent; border: none; cursor: pointer;
      color: var(--tb-text-muted, #97a0b0); border-radius: var(--tb-radius-sm, 8px);
      transition: color var(--tb-motion-fast, 120ms), background var(--tb-motion-fast, 120ms);
      overflow: visible;
    }
    .icon-btn:hover { color: var(--tb-text-primary, #e6e9ef); background: var(--tb-surface-inset, #11141a); }
    .icon-btn:focus-visible {
      outline: 2px solid var(--tb-brand-accent, #4f86f7);
      outline-offset: 2px;
    }
    .icon-btn .material-symbols-outlined { font-size: 1.15rem; line-height: 1; pointer-events: none; }
    @media (prefers-reduced-motion: reduce) { .icon-btn { transition: none; } }
    /* Hide density toggle at xs — it's secondary on smallest screens. theme-toggle is exempt
       (pre-existing); debug-drawers-toggle is ALSO exempt (owner UX critique, 2026-09-14 fix
       follow-up) -- it is the only reachable path to the debug wrapper's mock-phone/HTTP-log
       drawers (needed for step-up ceremonies even on a phone-width viewport, per
       debug-shell.component.ceremony-phone.spec.ts's narrow-viewport carve-out), and it is bound
       to the Alt+Shift+D shortcut, which must keep working at every viewport width -- the old
       dedicated header bar it replaced was never subject to this rule at all. */
    @media (max-width: 480px) {
      .icon-btn:not([data-testid='theme-toggle']):not([data-testid='debug-drawers-toggle']) { display: none; }
    }

    /* ── Language selector (navbar-language-selector) ── */
    .lang-selector { position: relative; display: inline-flex; flex-shrink: 0; }
    .lang-selector.open { z-index: calc(var(--tb-z-sticky, 200) + 1); }
    .lang-selector-trigger { padding: 0 6px; }
    .lang-flag {
      display: block; border-radius: 2px;
      /* Subtle outline so white-bordered flags don't vanish on dark bg */
      box-shadow: 0 0 0 1px rgba(255,255,255,0.15);
      object-fit: cover;
    }
    .lang-preview-label { margin-left: 0.35rem; font-size: 0.75rem; }
    /* Hide at xs alongside other secondary toggles */
    @media (max-width: 480px) { .lang-selector { display: none; } }

    /* ADR-0039 AI-2a: assistant launcher button in top bar */
    .assist-launcher {
      display: inline-flex; align-items: center; gap: 0.3rem;
      padding: 0.3rem 0.65rem;
      border-radius: var(--tb-radius-pill, 999px);
      border: 1px solid var(--tb-border-line, #2a2f3a);
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 12%, transparent);
      color: var(--tb-text-primary, #e6e9ef);
      cursor: pointer; font-family: inherit; font-size: 0.82rem; font-weight: 500;
      min-height: 32px; white-space: nowrap;
      transition: background var(--tb-motion-fast, 120ms), border-color var(--tb-motion-fast, 120ms);
    }
    .assist-launcher:hover {
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 22%, transparent);
      border-color: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 60%, transparent);
    }
    .assist-launcher:focus-visible {
      outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 2px;
    }
    @media (prefers-reduced-motion: reduce) { .assist-launcher { transition: none; } }
    .assist-launcher-icon {
      font-size: 1rem; flex-shrink: 0;
      color: var(--tb-accent-cosession-ai, #7C3AED);
      font-variation-settings: 'OPSZ' 20, 'wght' 400;
    }
    .assist-launcher-label { font-size: 0.82rem; }
    /* Track 5 leg 2: visible hotkey teaching chip (Ctrl+K / Cmd+K toggles the assistant) */
    .assist-hotkey-hint {
      font-family: var(--tb-font-mono, ui-monospace, monospace);
      font-size: 0.66rem;
      line-height: 1;
      padding: 0.18rem 0.32rem;
      border: 1px solid var(--tb-border-line, #2a2f3a);
      border-radius: var(--tb-radius-sm, 4px);
      color: var(--tb-text-muted, #97a0b0);
      background: var(--tb-surface-inset, #11141a);
      white-space: nowrap;
    }
    /* Keep the label visible even at sm — assistant is primary on mobile too */
    @media (max-width: 480px) {
      .assist-launcher-label { display: none; }
      /* No hardware keyboard to teach at phone width — drop the chip with the label. */
      .assist-hotkey-hint { display: none; }
    }

    /*
     * Assist panel: full-width takeover below the top bar on desktop.
     * Mobile: fixed bottom sheet.
     * Does NOT use position:fixed on desktop so it sits in document flow
     * below the sticky bar and does not overlap the left nav.
     */
    .assist-panel {
      /* flow-layout: width is the full shell column (content area) */
      width: 100%;
      box-sizing: border-box;
      z-index: 180;
      display: flex;
      flex-direction: column;
      gap: 0;
      background: var(--tb-surface-panel, #1a1d24);
      border-bottom: 2px solid color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 40%, transparent);
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.28);
    }
    /* Head: one thin identity strip, not a title block. Was 0.65rem/1.5rem with a 1rem heading;
       the panel's whole job is the input below it, and the head was competing for the same
       vertical budget as the (now removed) duplicate title row inside tb-command-box. */
    .assist-panel-head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.35rem 0.9rem;
      min-height: 40px;
      border-bottom: 1px solid var(--tb-border-line, #2a2f3a);
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 8%, var(--tb-surface-panel, #1a1d24));
    }
    .assist-panel-head h2 {
      margin: 0;
      font-size: 0.85rem;
      font-weight: 650;
      letter-spacing: 0.01em;
      color: var(--tb-text-primary, #e6e9ef);
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .assist-panel-robot {
      font-size: 1.05rem;
      color: var(--tb-accent-cosession-ai, #7C3AED);
      flex-shrink: 0;
    }
    .assist-tour-toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      flex-shrink: 0;
      min-height: 30px;
      padding: 0 0.6rem;
      font: inherit;
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--tb-text-muted, #97a0b0);
      background: var(--tb-surface-inset, #11141a);
      border: 1px solid var(--tb-border-line, #2a2f3a);
      border-radius: var(--tb-radius-pill, 999px);
      cursor: pointer;
    }
    .assist-tour-toggle:hover { color: var(--tb-text-primary, #e6e9ef); }
    .assist-tour-toggle:focus-visible {
      outline: 2px solid var(--tb-accent-cosession-ai, #7C3AED);
      outline-offset: 2px;
    }
    .assist-tour-toggle.on {
      color: var(--tb-accent-cosession-ai, #7C3AED);
      border-color: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 55%, transparent);
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 12%, transparent);
    }
    .assist-tour-toggle .material-symbols-outlined { font-size: 1rem; }

    /* The two columns. minmax(0, ...) on both tracks is what stops a long unbreakable string
       (an account label, a pasted command) from widening a track past the panel instead of
       scrolling inside it — 1fr alone means minmax(auto, 1fr), whose auto minimum is the
       content's intrinsic width. */
    .assist-panel-cols {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      align-items: start;
    }
    .assist-panel-cols.with-tour {
      /* The quest column's share. It was a 24rem cap against an unbounded assistant, which on a
         wide panel left the tour reading as a narrow gutter beside a very wide thread. Now both
         tracks are proportional with the tour given a real floor: it never drops below 22rem
         (the width its fixed-height rows and their detail text need to avoid wrapping into a
         scrollbar), and it grows to a third of the panel rather than staying pinned at 24rem.
         Talking to the assistant ABOUT the quests is the first-class case, so neither column is
         allowed to become a sliver. */
      grid-template-columns: minmax(0, 2fr) minmax(22rem, 1fr);
    }
    .assist-panel-body {
      padding: 0.85rem 0.9rem 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.6rem;
      min-width: 0;
      max-height: calc(100dvh - 12rem);
      overflow-y: auto;
      /* Anything that genuinely cannot wrap (code, a wide table) scrolls in its own box rather
         than pushing the panel sideways. */
      overflow-x: hidden;
    }
    .assist-tour-pane-label {
      margin: 0 0 0.5rem;
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--tb-text-muted, #97a0b0);
    }
    .assist-tour-pane {
      min-width: 0;
      padding: 0.85rem 0.9rem 0.75rem;
      border-left: 1px solid var(--tb-border-line, #2a2f3a);
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 4%, var(--tb-surface-panel, #1a1d24));
      max-height: calc(100dvh - 12rem);
      overflow-y: auto;
      overflow-x: hidden;
    }
    /* Narrow shells stack the columns; the tour goes FIRST because on a phone it is usually why
       the panel was opened, and it is the half with the "what do I do next" answer. */
    @media (max-width: 1100px) {
      .assist-panel-cols.with-tour { grid-template-columns: minmax(0, 1fr); }
      .assist-tour-pane {
        order: -1;
        border-left: none;
        border-bottom: 1px solid var(--tb-border-line, #2a2f3a);
        max-height: none;
      }
      .assist-panel-body { max-height: calc(100dvh - 20rem); }
    }
    /* Long, unbreakable content inside the panel wraps rather than overflowing. Applied to the
       text-bearing cards rather than a universal selector so genuinely tabular children keep their own rules. */
    .assist-panel p,
    .assist-panel li,
    .assist-panel .assist-thinking-cmd,
    .assist-panel .assist-decision code {
      overflow-wrap: anywhere;
      min-width: 0;
    }
    /* Track 5 leg 2: the single polite live region wrapping the results stack. Mirrors the
       panel-body column so wrapping the cards does not change their layout. */
    .assist-results {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .assist-results:empty { display: none; }
    .assist-close {
      width: 32px;
      height: 32px;
      margin-left: auto;
      /* Never the control that loses a width fight: a head that squeezes its own close button off
         the edge leaves a full-width panel with no way out. The title ellipsises and the tour
         toggle sheds its label first (below). */
      flex-shrink: 0;
    }
    /* Phone widths: the tour toggle keeps its flag glyph and drops the words. Four items in a
       340px strip is exactly where the close button used to get pushed out of view. */
    @media (max-width: 560px) {
      .assist-tour-toggle-label { display: none; }
      .assist-tour-toggle { padding: 0 0.45rem; }
    }
    /* Mobile: fixed bottom sheet */
    @media (max-width: 900px) {
      .assist-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        max-height: 70dvh;
        overflow-y: auto;
        border-bottom: none;
        border-top: 2px solid color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 40%, transparent);
      }
    }
    .assist-help-card,
    .assist-authorized,
    .assist-denied {
      display: flex;
      flex-direction: column;
      gap: 0.55rem;
      padding: 0.75rem;
      border-radius: var(--tb-radius-sm, 8px);
      border: 1px solid color-mix(in srgb, var(--tb-brand-accent, #4f86f7) 30%, transparent);
      background: color-mix(in srgb, var(--tb-brand-accent, #4f86f7) 5%, transparent);
    }
    .assist-help-head,
    .assist-authorized,
    .assist-denied {
      flex-direction: row;
      align-items: flex-start;
      gap: 0.6rem;
    }
    .assist-help-head {
      display: flex;
    }
    .assist-help-head > .material-symbols-outlined,
    .assist-authorized > .material-symbols-outlined,
    .assist-denied > .material-symbols-outlined {
      color: var(--tb-brand-accent, #4f86f7);
      font-size: 1.2rem;
      flex-shrink: 0;
    }
    .assist-help-copy {
      min-width: 0;
    }
    .assist-help-label {
      margin: 0 0 0.1rem;
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--tb-brand-accent, #4f86f7);
    }
    .assist-help-title,
    .assist-authorized-title,
    .assist-denied-title {
      margin: 0;
      font-size: 0.88rem;
      font-weight: 650;
      color: var(--tb-text-primary, #e6e9ef);
    }
    .assist-help-summary,
    .assist-authorized-copy,
    .assist-denied-body,
    .assist-denied-reason {
      margin: 0;
      font-size: 0.8rem;
      line-height: 1.45;
      color: var(--tb-text-muted, #97a0b0);
    }
    /* Denial follow-ups: who-can / why — legible next steps AS the user. */
    .assist-denied-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.4rem;
      flex-wrap: wrap;
    }
    .assist-denied-followup {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      min-height: 32px;
      padding: 0.32rem 0.7rem;
      border: 1px solid color-mix(in srgb, var(--tb-accent-cosession-ai, #7c3aed) 55%, transparent);
      border-radius: var(--tb-radius-sm, 8px);
      background: none;
      color: var(--tb-accent-cosession-ai, #9b7bf0);
      cursor: pointer;
      font: inherit;
      font-size: 0.78rem;
      font-weight: 600;
    }
    .assist-denied-followup:hover:not(:disabled) {
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7c3aed) 10%, transparent);
    }
    .assist-denied-followup:disabled { opacity: 0.55; cursor: default; }
    .assist-denied-followup:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); }
    .assist-denied-followup .material-symbols-outlined { font-size: 1rem; }
    .assist-denied-query-status {
      margin: 0.4rem 0 0;
      font-size: 0.78rem;
      color: var(--tb-text-muted, #97a0b0);
    }
    .assist-denied-query-error { color: var(--tb-finance-negative, #f7768e); }
    /* Honest thinking affordance: elapsed time + truthful slow hint; request/response
       backend — no fake streaming. Dots pause under prefers-reduced-motion. */
    .assist-thinking {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      padding: 0.75rem;
      border-radius: var(--tb-radius-sm, 8px);
      border: 1px solid color-mix(in srgb, var(--tb-accent-cosession-ai, #7c3aed) 30%, transparent);
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7c3aed) 6%, transparent);
    }
    .thinking-dots {
      display: inline-flex;
      gap: 4px;
      padding-top: 0.45rem;
      flex-shrink: 0;
    }
    .thinking-dots span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--tb-accent-cosession-ai, #7c3aed);
      animation: assist-thinking-pulse 1.2s infinite ease-in-out;
    }
    .thinking-dots span:nth-child(2) { animation-delay: 0.18s; }
    .thinking-dots span:nth-child(3) { animation-delay: 0.36s; }
    @keyframes assist-thinking-pulse {
      0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
      40% { opacity: 1; transform: scale(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      .thinking-dots span { animation: none; opacity: 0.8; }
    }
    .assist-thinking-copy { min-width: 0; }
    .assist-thinking-title {
      margin: 0;
      font-size: 0.85rem;
      font-weight: 650;
      color: var(--tb-text-primary, #e6e9ef);
    }
    .thinking-elapsed {
      font-weight: 400;
      color: var(--tb-text-muted, #97a0b0);
      font-variant-numeric: tabular-nums;
    }
    .assist-thinking-cmd {
      margin: 0.15rem 0 0;
      font-size: 0.76rem;
      color: var(--tb-text-muted, #97a0b0);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .assist-thinking-hint {
      margin: 0.35rem 0 0;
      font-size: 0.76rem;
      color: var(--tb-finance-pending, #e0af68);
    }
    .assist-help-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .assist-open-help,
    .assist-suggestion {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      min-height: 32px;
      padding: 0.32rem 0.7rem;
      border: 1px solid var(--tb-brand-accent, #4f86f7);
      border-radius: var(--tb-radius-sm, 8px);
      background: none;
      color: var(--tb-brand-accent, #4f86f7);
      cursor: pointer;
      font: inherit;
      font-size: 0.8rem;
      font-weight: 600;
    }
    .assist-open-help:hover,
    .assist-suggestion:hover {
      background: color-mix(in srgb, var(--tb-brand-accent, #4f86f7) 12%, transparent);
    }
    .assist-open-help:focus-visible,
    .assist-suggestion:focus-visible,
    .assist-dismiss:focus-visible {
      outline: 2px solid var(--tb-brand-accent, #4f86f7);
      outline-offset: 2px;
    }
    .assist-open-help .material-symbols-outlined,
    .assist-suggestion .material-symbols-outlined {
      font-size: 0.95rem;
    }
    .assist-dismiss {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border: 0;
      border-radius: var(--tb-radius-sm, 6px);
      background: transparent;
      color: var(--tb-text-muted, #97a0b0);
      cursor: pointer;
    }
    .assist-suggestion-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
    }
    .assist-suggestion {
      border-color: var(--tb-border-line, #2a2f3a);
      color: var(--tb-text-muted, #97a0b0);
      background: var(--tb-surface-inset, #11141a);
    }
    .assist-authorized {
      border-color: color-mix(in srgb, var(--tb-finance-positive, #8fe388) 35%, transparent);
      background: color-mix(in srgb, var(--tb-finance-positive, #8fe388) 7%, transparent);
    }
    .assist-authorized > .material-symbols-outlined {
      color: var(--tb-finance-positive, #8fe388);
    }
    .assist-denied {
      border-color: color-mix(in srgb, var(--tb-finance-negative, #f7768e) 38%, transparent);
      background: color-mix(in srgb, var(--tb-finance-negative, #f7768e) 8%, transparent);
    }
    .assist-denied > .material-symbols-outlined {
      color: var(--tb-finance-negative, #f7768e);
    }
    .assist-denied-copy {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      min-width: 0;
    }
    .assist-denied-reason {
      padding-top: 0.1rem;
    }
    .assist-denied-reason span {
      color: var(--tb-finance-negative, #f7768e);
      font-weight: 650;
    }
    .assist-decision {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.4rem;
      padding: 0.35rem 0.6rem;
      border: 1px solid var(--tb-border-line, #2a2f3a);
      border-radius: var(--tb-radius-sm, 8px);
      color: var(--tb-text-muted, #97a0b0);
      font-size: 0.78rem;
    }
    .assist-decision code {
      color: var(--tb-text-primary, #e6e9ef);
      font-family: var(--tb-font-mono, ui-monospace, monospace);
      font-size: 0.72rem;
    }


    /* ADR-0044 GUIDE route: navigation-guide card in the shell assistant panel */
    .assist-guide-card {
      display: flex;
      flex-direction: column;
      gap: 0.55rem;
      padding: 0.75rem;
      border-radius: var(--tb-radius-sm, 8px);
      border: 1px solid color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 30%, transparent);
      background: color-mix(in srgb, var(--tb-accent-cosession-ai, #7C3AED) 5%, transparent);
    }
    .assist-guide-head {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 0.55rem;
    }
    .assist-guide-head > .material-symbols-outlined {
      color: var(--tb-accent-cosession-ai, #7C3AED);
      font-size: 1.15rem;
      flex-shrink: 0;
      margin-top: 0.1rem;
    }
    .assist-guide-copy {
      flex: 1;
      min-width: 0;
    }
    .assist-guide-label {
      margin: 0 0 0.1rem;
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--tb-accent-cosession-ai, #7C3AED);
    }
    .assist-guide-title {
      margin: 0;
      font-size: 0.88rem;
      font-weight: 650;
      color: var(--tb-text-primary, #e6e9ef);
    }
    .assist-guide-breadcrumb {
      margin: 0.1rem 0 0;
      font-size: 0.72rem;
      color: var(--tb-text-muted, #97a0b0);
      font-family: var(--tb-font-mono, ui-monospace, monospace);
    }
    .assist-guide-steps {
      margin: 0;
      padding-left: 1.3rem;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    .assist-guide-step {
      font-size: 0.8rem;
      color: var(--tb-text-primary, #e6e9ef);
      line-height: 1.4;
    }
    .assist-guide-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .assist-guide-go-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      min-height: 32px;
      padding: 0.3rem 0.7rem;
      border: none;
      border-radius: var(--tb-radius-sm, 8px);
      background: var(--tb-accent-cosession-ai, #7C3AED);
      color: #fff;
      cursor: pointer;
      font: inherit;
      font-size: 0.8rem;
      font-weight: 600;
    }
    .assist-guide-go-btn:hover {
      opacity: 0.9;
    }
    .assist-guide-go-btn:focus-visible {
      outline: 2px solid var(--tb-accent-cosession-ai, #7C3AED);
      outline-offset: 2px;
    }
    .assist-guide-go-btn .material-symbols-outlined {
      font-size: 0.95rem;
    }

    /* ── Body / content area ── */
    .body {
      display: flex; flex: 1; min-width: 0; min-height: 0;
    }
    .content-wrap {
      flex: 1; display: flex; flex-direction: column; min-width: 0; min-height: 0; overflow-y: auto;
      padding: 1.25rem; box-sizing: border-box;
    }
    .banner-stack {
      position: relative;
      z-index: var(--tb-z-sticky, 200);
      min-width: 0;
    }
    /* At desktop the router outlet fills the space; at mobile the drawer overlays.
       The assist takeover panel ALSO offsets by the nav-rail width so it occupies the
       CONTENT area only (full width MINUS the left nav) and never overlaps the fixed
       left nav rail. (At <=900px the panel becomes a fixed bottom sheet — see its own
       media query — so this offset only applies at desktop.) */
    @media (min-width: 901px) {
      .body,
      .banner-stack,
      .assist-panel { margin-left: var(--tb-nav-rail-width, 212px); }
      /* The panel is a column-flex item of .shell (align-self:stretch); drop the explicit
         width:100% at desktop so the margin-left actually shrinks it (100% + margin would
         overflow past the right edge). The flex stretch + margin-left now size it to the
         content region exactly: full width MINUS the left nav rail. */
      .assist-panel { width: auto; }
    }

    /* Logout-failure banner (mirrors factor-expiry-banner.component.ts's negative-state idiom). */
    .logout-error-banner {
      display: flex; align-items: center; gap: 0.6rem;
      padding: 0.6rem 1rem; font-size: 0.85rem; font-weight: 500;
      background: color-mix(in srgb, var(--tb-finance-negative, #f7768e) 18%, transparent);
      border-bottom: 1px solid color-mix(in srgb, var(--tb-finance-negative, #f7768e) 40%, transparent);
      color: var(--tb-finance-negative, #f7768e);
    }
    .logout-error-banner .banner-icon { font-size: 1.1rem; flex-shrink: 0; }
    .logout-error-banner .banner-text { flex: 1; color: inherit; }
    .logout-error-banner .banner-action {
      cursor: pointer; font-family: inherit; text-decoration: none; font-weight: 700; white-space: nowrap;
      padding: 0.2rem 0.55rem; border-radius: 4px;
      border: 1px solid currentColor; background: none; color: inherit;
      font-size: 0.8rem;
    }
    .logout-error-banner .banner-action:hover:not(:disabled) { opacity: 0.75; }
    .logout-error-banner .banner-action:disabled { opacity: 0.5; cursor: not-allowed; }
    .logout-error-banner .banner-action:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
    .logout-error-banner .banner-dismiss {
      background: none; border: none; cursor: pointer; padding: 0.15rem;
      color: inherit; display: flex; align-items: center; opacity: 0.7;
      justify-content: center; flex-shrink: 0;
      min-width: var(--tb-touch, 48px); min-height: var(--tb-touch, 48px);
    }
    .logout-error-banner .banner-dismiss:hover { opacity: 1; }
    .logout-error-banner .banner-dismiss:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
    .logout-error-banner .banner-dismiss .material-symbols-outlined { font-size: 1rem; }

    /* ── Nav inside tb-mobile-nav's drawer ── */
    .nav { display: flex; flex-direction: column; gap: 0; }
    .nav-section {
      display: flex; flex-direction: column; gap: 0;
      padding-bottom: var(--tb-space-2); margin-bottom: var(--tb-space-2);
      border-bottom: 1px solid var(--tb-border-line, #2a2f3a);
    }
    .nav-section:last-child { border-bottom: none; padding-bottom: 0; margin-bottom: 0; }
    .nav-section-header {
      font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
      color: var(--tb-text-muted, #9aa5b1); padding: 0.25rem 0.7rem 0.35rem;
      display: block;
    }
    .nav a {
      padding: var(--tb-space-1) var(--tb-space-3);
      /* Desktop navigation is denser in both modes; retain roomy coarse-pointer targets. */
      box-sizing: border-box;
      min-height: calc(var(--tb-touch, 48px) - var(--tb-space-2)); display: flex; align-items: center;
      border-radius: var(--tb-radius-sm, 6px);
      color: var(--tb-text-muted, #9aa5b1); text-decoration: none;
      font-weight: 500; font-size: 0.9rem;
    }
    .nav a:hover { background: var(--tb-surface-inset, #11141b); color: inherit; }
    .nav a.on { background: var(--tb-surface-inset, #11141b); color: var(--tb-text-primary, #e6e9ef); }
    @media (pointer: coarse), (max-width: 900px) {
      .nav a { min-height: 44px; }
    }
    .nav-cta { color: var(--tb-brand-accent, #4f86f7) !important; font-weight: 600 !important; }
    .nav-cta.on, .nav-cta:hover {
      background: color-mix(in srgb, var(--tb-brand-accent, #4f86f7) 12%, transparent) !important;
    }
    .nav a.nav-cta { color: var(--tb-text-muted, #9aa5b1) !important; font-weight: 500 !important; }
    .nav a.nav-cta.on, .nav a.nav-cta:hover {
      background: var(--tb-surface-inset, #11141b) !important;
    }
  `,
})
export class AppComponent implements OnInit {
  readonly store = inject(AppStore);
  readonly townSession = computed(() => isOpaqueTownsimMe(this.store.user())
    || (typeof window !== 'undefined' && isConfiguredSimHost(window.location.hostname)));
  readonly townContextKey = computed(() => JSON.stringify([
    this.store.user()?.memberHandle ?? null, this.store.personaEpoch(), this.store.activeOrgId(),
  ]));
  readonly townProjection = signal<{ contextKey: string; selection: TownsimSessionSelection | null } | null>(null);
  readonly townSelection = computed(() => this.townProjection()?.contextKey === this.townContextKey()
    ? this.townProjection()?.selection ?? null : null);
  readonly stepUpCeremony = inject(StepUpCeremonyService);
  private readonly cosessionDiscovery = inject(CosessionDiscoveryService);
  readonly consentAttention = inject(CosessionConsentAttentionService);
  readonly consentBannerReady = signal(false);
  readonly pendingCosessionConsent = computed(() => (this.cosessionDiscovery.state().requests?.length ?? 0) > 0);
  private readonly api = inject(ApiService);
  // Embed contract v1 (df2dc530): instantiating the service arms ?embed=1 chrome suppression +
  // the one-way iframe→parent postMessage events. No-op outside embed mode.
  private readonly embed = inject(EmbedService);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly entCtx = inject(EntitlementContextService);
  /**
   * ENGINE-fed capability source (PBAC+ReBAC). Drives entitlement-gated nav + dashboard widgets +
   * the not-entitled-in-place panel — NEVER Keycloak realm roles. Exposed to the template as
   * {@link capabilities} so nav links can gate via {@code capabilities.can('CODE')}.
   */
  readonly capabilities = inject(EntitlementCapabilityService);
  readonly customerSurfaceAccess = inject(CustomerSurfaceAccessService);
  readonly consolidationConsents = inject(ConsolidationConsentStore);
  /** Engine action-code constants for the template's nav gating (e.g. CODES.VIEW_ACCOUNT). */
  protected readonly CODES = ENGINE_ACTION;
  /**
   * XT-0912 #2: ENVIRONMENT-off capability tracker (separate axis from the ENGINE-fed
   * {@link capabilities} above — this is "is the feature flag on in THIS deployment", not
   * "does this persona hold the grant"). "Payment orders" was a proven dead end: the nav
   * always showed it under INITIATE_PAYMENT, but the endpoint answers 503 TB-UP-0004 whenever
   * the capability is flagged off, regardless of persona. Once the page has hit that 503 once
   * this session, the nav stops offering the link.
   */
  readonly capabilityAvailability = inject(CapabilityAvailabilityService);
  /** Template-visible key constant (module-scope consts aren't reachable from the template). */
  protected readonly PAYMENT_ORDERS_CAPABILITY_KEY = PAYMENT_ORDERS_CAPABILITY_KEY;
  protected readonly INSTANT_PAYMENTS_CAPABILITY_KEY = INSTANT_PAYMENTS_CAPABILITY_KEY;
  /** XT-0912 3(a): same environment-off axis as above, for the "Ask insights" nav entry --
   *  mirrors edge-bff's `toybank.insights.enabled` ops kill-switch. */
  protected readonly INSIGHTS_ASK_CAPABILITY_KEY = INSIGHTS_ASK_CAPABILITY_KEY;
  /** Walkabout 2026-09-28: dark features leave the nav instead of leading to a 503/404 page. */
  protected readonly ACH_COLLECTIONS_CAPABILITY_KEY = ACH_COLLECTIONS_CAPABILITY_KEY;
  protected readonly REPORT_DISTRIBUTION_CAPABILITY_KEY = REPORT_DISTRIBUTION_CAPABILITY_KEY;
  protected readonly IMPLEMENTATIONS_CAPABILITY_KEY = IMPLEMENTATIONS_CAPABILITY_KEY;
  /** "Live platform insights" mode — held here only so a non-staff identity can be forced OFF. */
  private readonly insightsMode = inject(InsightsModeService);
  /**
   * Presentation-only: is the signed-in identity a bank employee rather than a customer-company
   * user? Mirrors the server's exclusive audience rule (@toybank/ui isExclusiveBankStaffRoles →
   * IdentityAudienceGuard). NEVER a capability gate — it decides whether a piece of top-bar chrome
   * is worth rendering, nothing more. A demo persona carries `demo-persona`, so the exclusive rule
   * keeps every demo session out even if a staff-shaped role name rides along.
   */
  readonly isBankStaff = computed(() => isExclusiveBankStaffRoles(this.store.user()?.roles));
  /** ADR-0143 customer-control-plane role gate. PBAC remains a separate required check. */
  readonly isCompanyAdmin = computed(() =>
    (this.store.user()?.roles ?? []).some(
      (role) => role.replace(/^ROLE_/i, '').toLowerCase() === 'company-admin',
    ),
  );
  /** @toybank/ui's shared human action-label map (WP-E gap: the assist-denial panel rendered the
   *  raw engine enum in a <code> tag; humans read "Initiate payments", not INITIATE_PAYMENT). */
  protected readonly actionLabel = actionLabel;
  private readonly notifService = inject(NotificationService);
  private readonly assistHistory = inject(AssistHistoryService);
  private readonly assistInvitation = inject(AssistInvitationService);
  private readonly destroyRef = inject(DestroyRef);
  /** Session-log thread (ADR-0039 D7) — public: the template renders its computed views. */
  readonly assistThread = inject(AssistThreadService);
  private readonly assistQueryService = inject(AssistQueryService);
  private readonly guideHighlight = inject(GuideHighlightService);
  private readonly masking = inject(TB_MASKING_PREFERENCE);
  readonly passkeyService = inject(PasskeyDetectionService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  /** Dark/dense wave: theme + density services (injected for use in template). */
  readonly themeService = inject(ThemeService);
  readonly densityService = inject(DensityService);
  /**
   * The debug wrapper's paired-drawers toggle (owner UX critique, 2026-09-14): present ONLY when
   * this component is mounted inside `DebugShellComponent` (which provides {@link TB_DEBUG_DRAWERS}
   * at its element-injector level -- see debug/debug-drawers.model.ts). Absent in the bff build, so
   * the `manage_accounts` top-bar icon renders nowhere near a plain customer/demo session.
   */
  readonly debugDrawers: DebugDrawersController | null = inject(TB_DEBUG_DRAWERS, { optional: true });
  private readonly educationMode = inject(EducationModeService);
  private readonly entitlementMode = inject(EntitlementModeService);
  readonly overlaysEnabled = computed(() => this.educationMode.enabled() && this.entitlementMode.enabled());

  /** Keep the two explanatory layers in lockstep while their services remain independently usable. */
  toggleOverlays(): void {
    const next = !this.overlaysEnabled();
    this.educationMode.set(next);
    this.entitlementMode.set(next);
  }

  /** Migrate the two retired preferences into the one combined control on first render. */
  private synchronizeOverlayPreferences(): void {
    const enabled = this.educationMode.enabled() || this.entitlementMode.enabled();
    this.educationMode.set(enabled);
    this.entitlementMode.set(enabled);
  }
  /** Instantiates the runtime language guard so every view defaults to English unless explicitly changed. */
  readonly languageService = inject(LanguageService);
  readonly organizationSegment = inject(OrganizationSegmentService);

  /**
   * Mid-session "Switch Demo User" backing. The debug wrapper provides itself via
   * TB_PERSONA_SWITCHER (debug build); when that token is absent (the bff build) we fall
   * back to the root-provided BFF-backed DemoPersonaSwitchService. Importing the BFF service
   * statically is safe for FE-3 tree-shaking — it carries NO debug PersonaService dependency.
   */
  readonly demoPersonaSwitcher = inject(DemoPersonaSwitchService);
  private readonly personaSwitcher: PersonaSwitcher =
    inject(TB_PERSONA_SWITCHER, { optional: true }) ?? this.demoPersonaSwitcher;
  /**
   * Host-level env gate (client-side mirror of the edge-bff EnvModeFilter): mid-session
   * persona switching applies only on demo/sim (+ their debug variants), never on dev.
   */
  private readonly personaSwitchHostApplies =
    typeof window !== 'undefined' && personaSwitchAppliesToHost(window.location.hostname);
  readonly isPublicDemoHost =
    typeof window !== 'undefined' && isPublicDemoHostname(window.location.hostname);
  /** The SAME shared branding source the debug wrapper's per-target login preview uses
   * (login/login-screen.component.ts) -- picks the Main vs Demo Portal variant by host, the
   * only two of the five LOGIN_PRESENTATION entries this project owns real wordmark art for. */
  readonly loginPresentation: LoginPresentation =
    LOGIN_PRESENTATION[this.isPublicDemoHost ? 'Demo Portal' : 'Main Portal'];
  /** The shared login screen's username field. Real portal: optional -- forwarded as an OIDC
   * login_hint (see #login) that pre-fills Keycloak's hosted login page; never required, since
   * Keycloak (not this SPA) is the actual credential-entry surface. */
  readonly loginUsername = signal('');
  /** The shared login screen's injected submit strategy for this surface: redirect to Keycloak. */
  readonly loginSubmitStrategy: LoginSubmitStrategy = { submit: () => this.login() };
  /** Open/closed state of the top-bar switch menu. */
  readonly personaSwitchOpen = signal(false);

  /**
   * F32 integration seam: a checklist "Switch to X" click requests switcher focus; opening the
   * panel here lets tb-demo-persona-map consume the request (scroll + focus the row).
   */
  private readonly switcherFocusOpenEffect = effect(() => {
    // Optional call: spec harnesses stub DemoPersonaSwitchService partially.
    if (this.demoPersonaSwitcher.switcherFocusRequest?.()) this.personaSwitchOpen.set(true);
  });

  readonly sessionLoading = signal(true);
  /** Sign-in page status message (flag only; debugWrapper is the debug shell's business). */
  readonly loginNotices = inject(LoginNoticeService);
  private readonly loginNoticeLoad = effect(() => {
    if (!this.sessionLoading() && !this.store.user()) untracked(() => this.loginNotices.ensureLoaded());
  });
  /**
   * True when the most recent {@link logout} attempt failed server-side. A failed logout must
   * never look successful (a silently-resumed session is worse than a visible error) -- so on
   * error we do NOT navigate to /signed-out and instead surface this in the banner-stack with a
   * retry affordance. Cleared on a fresh logout attempt and on success.
   */
  readonly logoutError = signal(false);
  /**
   * True while a logout POST is in flight — disables BOTH logout affordances (the top-bar button
   * and the banner retry) against double-submit, and short-circuits {@link logout} itself.
   *
   * <p>The handler guard is the load-bearing one: it also covers a programmatic call and the
   * window before change detection re-renders the disabled attribute.
   */
  readonly loggingOut = signal(false);
  /** Demo and debug-demo portals do not exist in the DOM until instance + acting identity are ready. */
  readonly demoBootstrapLocked = computed(
    () => this.personaSwitchHostApplies && !this.demoPersonaSwitcher.bootstrapReady(),
  );
  /**
   * The demo root gate replaces the shell when (a) a signed-in visitor's preparation is in flight
   * or failed, or (b) the boot probe itself failed ('unavailable') -- except on public routes
   * (recovery, KYC /open, signed-out), which never depend on the demo session and must stay
   * reachable while the demo service is down. A 'signed-out' probe never shows the gate: that
   * visitor gets the ordinary sign-in landing with no error.
   */
  readonly demoGateVisible = computed(() => {
    if (!this.demoBootstrapLocked()) return false;
    if (this.demoPersonaSwitcher.rootSessionDetected() || this.store.user()) return true;
    return this.demoPersonaSwitcher.bootstrapState() === 'unavailable' && !this.isPublicRoute();
  });
  /** Actual-login subject whose persona-ready shell services have already been started. */
  private completedDemoRootSubject: string | null = null;
  /**
   * ADR-0036 S7 first-login factor gate. The flag lives in a shared service so the
   * dedicated /login/enroll-factor screen can clear it on success (the gate is a
   * dedicated screen, not an in-shell modal). `needsFactorEnrollment` aliases the
   * service's `needsEnrollment` signal for callers that reference this property name.
   */
  private readonly enrollmentGate = inject(FactorEnrollmentGateService);
  private readonly factorService = inject(FactorService);
  readonly needsFactorEnrollment = this.enrollmentGate.needsEnrollment;
  readonly currentUrl = signal(this.router.url);
  /** Platform advisories v1: the current route's backing-service ids (route-services.ts),
   *  reactive off currentUrl() — feeds tb-advisory-banner's WARN/INFO route-scoping. */
  readonly currentRouteServices = computed(() => routeServices(this.currentUrl()));
  /** True when the user must be sent to the dedicated enrollment screen. */
  readonly showFactorEnrollmentGate = computed(() =>
    this.needsFactorEnrollment() && !isDeferredFactorEnrollmentRoute(this.currentUrl()),
  );
  /**
   * True when the current route is a pre-auth/public surface that must render even with
   * no BFF session. Reactive off currentUrl() (kept in sync via router events in the
   * constructor), so it updates on navigation. Drives the minimal unauthenticated shell
   * branch that mounts <router-outlet /> when store.user() is null.
   */
  readonly isPublicRoute = computed(() => isPublicShellRoute(this.currentUrl()));
  readonly shellOrgOptions = computed(() => this.store.orgs().map((org) => ({
    orgId: org.orgId, label: this.orgLabel(org),
  })));
  readonly shellOrgSelectedLabel = computed(() => this.shellOrgOptions()
    .find((org) => org.orgId === this.store.activeOrgId())?.label ?? this.store.activeOrgId());
  readonly navOpen = signal(false);
  readonly assistantOpen = signal(false);

  // ── Set-up tour dock (the guided-onboarding checklist, beside the assistant) ───────────────
  private readonly guidedOnboarding = inject(GuidedOnboardingService);
  /**
   * Does this session have a set-up tour at all? Drives the head toggle AND the second column.
   *
   * Reads `eligibleNature()` — a SYNCHRONOUS derivation from the demo session and /api/me — and
   * deliberately NOT the service's `presence` signal. `presence` is written by the checklist
   * component, which now only mounts inside the tour column: gating the column on it would mean
   * the column can never open, because the thing that says "there is a tour" is the thing the
   * tour column renders. Eligibility does not have that circularity and answers the question the
   * chrome actually asks — is this a demo (N0) or invited (N1) session? — with no probe.
   */
  readonly tutorialAvailable = computed(() => this.guidedOnboarding.eligibleNature() !== null);
  private readonly tutorialPaneRequested = signal(false);
  /**
   * The tour column shows when the visitor asked for it AND this session actually has a tour.
   * Deliberately NOT open-by-default: the panel's primary job is still the command input, and a
   * checklist that opens itself every time is the "bombarded with text" complaint in a new place.
   * {@link openAssistantWithTour} is the one path that opens both at once — used by the demo
   * welcome card's "Open the set-up tour" shortcut, where opening the tour IS the request.
   */
  readonly tutorialPaneOpen = computed(() => this.tutorialAvailable() && this.tutorialPaneRequested());

  toggleTutorialPane(): void {
    this.tutorialPaneRequested.update((open) => !open);
  }

  /** Open the assistant panel with the set-up tour already docked (welcome-card shortcut). */
  openAssistantWithTour(): void {
    this.tutorialPaneRequested.set(true);
    this.openAssistant();
  }
  readonly assistHistoryOpen = signal(false);
  /** Language selector dropdown state. */
  readonly langMenuOpen = signal(false);
  /** The BFF evaluates the actual login and configured public demo host, even in persona mode. */
  readonly staffLanguagePreview = computed(() => {
    const session = this.demoPersonaSwitcher.session();
    return !!this.store.user() && session?.enabled === true
      && session.staffLanguagePreview === true && !!session.realSubject?.trim();
  });
  private readonly languageMenuHost = viewChild('languageMenuHost', { read: ViewContainerRef });
  private readonly loadLanguageMenu = effect((onCleanup) => {
    const host = this.languageMenuHost();
    const staffPreview = this.staffLanguagePreview();
    if (!host) return;
    let cancelled = false;
    onCleanup(() => { cancelled = true; host.clear(); });
    void import('./i18n/language-menu.component').then(({ LanguageMenuComponent }) => {
      if (cancelled) return;
      const menu = host.createComponent(LanguageMenuComponent);
      menu.setInput('staffPreview', staffPreview);
      menu.instance.closed.subscribe(() => this.langMenuOpen.set(false));
      menu.changeDetectorRef.detectChanges();
      (menu.location.nativeElement as HTMLElement).querySelector<HTMLButtonElement>('.active')?.focus();
    }).catch(() => { if (!cancelled) this.langMenuOpen.set(false); });
  });
  private previewRootSubject: string | null = null;
  private readonly synchronizeLanguagePreview = effect(() => {
    const allowed = this.staffLanguagePreview();
    const root = allowed ? this.demoPersonaSwitcher.session()?.realSubject ?? null : null;
    untracked(() => {
      if (root !== this.previewRootSubject) this.languageService.setPreviewSegment(null);
      this.previewRootSubject = root;
      this.languageService.setPreviewAllowed(allowed);
    });
  });

  /** Dark/dense wave: theme icon and label helpers (used in template). */
  readonly themeIcon = () => THEME_ICON[this.themeService.preference()];
  readonly themeLabel = () => THEME_LABEL[this.themeService.preference()];

  /** Language selector: endonym display names for each configured language. */
  readonly langDisplayName: Record<AppLang, string> = {
    en: 'English',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch',
  };
  /** Language selector: flag asset path for each configured language. */
  readonly langFlagSrc: Record<AppLang, string> = {
    en: 'assets/flags/en.svg',
    es: 'assets/flags/es.svg',
    fr: 'assets/flags/fr.svg',
    de: 'assets/flags/de.svg',
  };

  @HostListener('document:click', ['$event'])
  closeLangMenuOnDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (this.langMenuOpen() && target instanceof Node && !this.hostEl.nativeElement.contains(target)) {
      this.langMenuOpen.set(false);
    }
    // Close the persona-switch panel when the click lands outside the top nav bar.
    // The panel is a child of .bar, so any click inside .bar (trigger, panel, other nav items)
    // is NOT a close event — the trigger's own (click) toggle handles open/close within .bar.
    if (this.personaSwitchOpen() && target instanceof Node) {
      const bar = this.hostEl.nativeElement.querySelector('.bar');
      if (bar && !bar.contains(target)) {
        this.personaSwitchOpen.set(false);
      }
    }
  }

  @HostListener('document:keydown.escape')
  closePersonaSwitchOnEscape(): void {
    if (this.personaSwitchOpen()) {
      this.personaSwitchOpen.set(false);
    }
  }

  toggleLangMenu(event: Event): void {
    event.stopPropagation();
    this.langMenuOpen.update((open) => !open);
  }

  /**
   * "Switch Demo User" visibility: env applies (demo/sim host) AND the active switcher
   * reports it is available (the BFF/debug switcher only flips available when there is a
   * live demo session with more than one persona). Both gates must hold.
   */
  readonly showPersonaSwitch = computed(
    () => this.personaSwitchHostApplies && this.personaSwitcher.available(),
  );
  readonly personaSwitchOptions = computed(() => this.personaSwitcher.options());
  readonly personaSwitchActiveKey = computed(() => this.personaSwitcher.activeKey());
  readonly personaSwitchBusy = computed(() => this.personaSwitcher.switching());
  readonly demoOrgName = computed(() => demoOrgNameFromSession(this.demoPersonaSwitcher.session()));

  /**
   * demo98: label of the currently-active demo persona for the fixed-width display
   * immediately left of the "Switch Demo User" dropdown. Resolves from the options list
   * so it stays in sync with whatever the switcher reports. Falls back to the real user's
   * name (store.user().name) when no persona is active (real-user session), and to a
   * placeholder when even that is unavailable.
   */
  readonly activePersonaLabel = computed((): string => {
    const key = this.personaSwitchActiveKey();
    if (key) {
      const opt = this.personaSwitchOptions().find((o) => o.key === key);
      if (opt) return opt.label;
    }
    return this.store.user()?.name ?? '— no persona —';
  });

  /** F39: single-character avatar-or-initial for the persona-switch trigger's compact badge. */
  readonly activePersonaInitial = computed((): string => {
    const label = this.activePersonaLabel().trim();
    return label ? label.charAt(0).toUpperCase() : '?';
  });

  /** F39: first name only, for the compact companion shown once the full label collapses. */
  readonly activePersonaFirstName = computed((): string => {
    const label = this.activePersonaLabel().trim();
    return label ? label.split(/\s+/)[0] : '—';
  });

  /**
   * ADR-0039 AI-2a: global shell command modal state.
   * The assistant lives as an inline shell panel so it can assist the current page without
   * forcing a route change.
   */
  readonly shellPendingResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellDeniedResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellEntitlementQueryResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellRmReadResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellAccountReadResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellInsightsResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellHelpResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellGuideResponse = signal<ResolveIntentResponse | null>(null);
  readonly shellClarifyWithSuggestions = signal<ResolveIntentResponse | null>(null);
  readonly shellLastDecision = signal<AssistDecision | null>(null);
  /** Denial follow-up (who-can / why) state — pure reads via /api/assist/query. */
  readonly shellDeniedQueryResult = signal<AssistQueryResponse | null>(null);
  readonly shellDeniedQueryLoading = signal<'WHO_CAN_QUERY' | 'EXPLAIN_DECISION' | null>(null);
  readonly shellDeniedQueryError = signal<string | null>(null);
  readonly THINKING_SLOW_HINT_SECONDS = THINKING_SLOW_HINT_SECONDS;
  /** The thinking turn opened by the latest command-box submission (one in flight max). */
  private shellPendingTurnId: string | null = null;
  private readonly shellShowMeHowResponses = new WeakSet<ResolveIntentResponse>();
  /** Track 5: responses whose CANCELLED emit came from "Rephrase" (skip the decision chip). */
  private readonly shellRephraseResponses = new WeakSet<ResolveIntentResponse>();
  private readonly shellCommandBox = viewChild<TbCommandBoxComponent>('shellCommandBox');
  private readonly assistDeliveryEpoch = signal(0);
  private readonly pendingAssistCommand = signal<{ text: string; submit: boolean; context: string } | null>(null);

  constructor() {
    // Discovery belongs to the eager shell, so an incoming invitation can trigger the
    // deferred banner. Retain exactly the authenticated-shell lifetime the banner had.
    effect((onCleanup) => {
      if (this.townSession() || this.sessionLoading() || this.demoGateVisible() || !this.store.user()
        || (this.isPublicRoute() && this.currentUrl().startsWith('/open'))) return;
      const connection = untracked(() => this.cosessionDiscovery.connect());
      const attention = this.consentAttention.requests$.subscribe(() => {
        if (!this.consentBannerReady()) this.cosessionDiscovery.refresh();
      });
      onCleanup(() => {
        attention.unsubscribe();
        connection.unsubscribe();
        this.consentBannerReady.set(false);
        this.consentAttention.consumePendingAttention();
      });
    });
    effect(() => {
      const box = this.shellCommandBox();
      const pending = this.pendingAssistCommand();
      if (!pending) return;
      if (!this.assistantOpen() || pending.context !== this.assistCommandContext()) {
        this.pendingAssistCommand.set(null);
        return;
      }
      if (!box) return;
      untracked(() => {
        this.pendingAssistCommand.set(null);
        if (pending.submit) box.submitCommand(pending.text);
        else box.restoreCommand(pending.text);
      });
    });
    this.assistInvitation.requested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((command) => {
      this.openAssistant();
      const context = this.assistCommandContext();
      // The panel creates the command box after this signal update. Leave the suggested
      // command editable; only the customer's Submit enters the prepare/review flow.
      setTimeout(() => this.deliverAssistCommand(command, false, context), 0);
    });
    this.synchronizeOverlayPreferences();
    this.currentUrl.set(this.router.url);
    this.router.events.subscribe((event) => {
      const nextUrl = shellUrlFromNavigationEvent(event, this.router.url);
      if (nextUrl !== null) this.currentUrl.set(nextUrl);
    });

    // Set-up tour open requests (the demo welcome card's shortcut). Counter-driven so a repeat
    // ask after the visitor closed the panel still opens it; skipped on the initial 0 so a fresh
    // session never opens the assistant by itself.
    effect(() => {
      if (this.guidedOnboarding.tourOpenRequest() > 0) {
        untracked(() => this.openAssistantWithTour());
      }
    });
    // Insights is staff-only chrome now (see isBankStaff). Hiding the toggle is not enough on its
    // own: InsightsModeService persists `tb.insightsMode` in localStorage, so a browser that had it
    // ON before this gate — or a staff session followed by a customer session in the same profile —
    // would keep rendering the overlay with no control left to turn it off. Force the layer off
    // whenever the identity is not staff; the preference is restored the moment a staff identity
    // toggles it back on.
    effect(() => {
      if (this.store.user() !== null && !this.isBankStaff() && this.insightsMode.enabled()) {
        this.insightsMode.set(false);
      }
    });
    // ENGINE-fed gating (PBAC+ReBAC demo-persona differentiation). The capability service reads the
    // active persona's OWN effective grants from the entitlement engine
    // (GET /api/entitlements/effective) and pushes the held action-code set into
    // EntitlementContextService, NOT `entCtx.setEntitlements(store.user()?.roles)` — gating off
    // Keycloak ROLE_* would defeat per-persona differentiation, since role checks don't vary by
    // active persona. Every entCtx.holds(...) consumer (dashboard widgets,
    // entitlement-source tooltips) is now engine-sourced, so persona switch -> what you can see/do
    // changes 1:1. Re-fires on login (user.sub appears) and org change; the persona-switch path
    // forces an explicit refresh (demo-persona-switch.service afterSwitchRefresh) because a same-org
    // hop keeps user.sub + activeOrgId unchanged.
    //
    // A TownSim (TOWNSIM_ORG) session's `/api/me` deliberately never carries `sub` — the whole
    // TownSim surface stays opaque-handle-only (MeController.personaMeBody; no raw subject/org
    // leaves the BFF for TownSim, unlike the demo-persona lane which does expose its own persona
    // subject here). The opaque member handle identifies the Town display profile. Its typed
    // scope must also match the current organization row from the same server-held authority.
    effect(() => {
      const me = this.store.user();
      const sub = me?.sub ?? me?.memberHandle ?? null;
      const org = this.store.activeOrgId();
      const epoch = this.store.personaEpoch();
      const revision = this.store.contextRevision();
      if (!sub || !org) { this.capabilities.refresh(null); return; }
      const town = me && isOpaqueTownsimMe(me);
      const townCodes = town ? (me.entitlements ?? []) : undefined;
      const townScope = town ? {
        projection: me.townCapabilities,
        memberHandle: me.memberHandle!,
        context: this.store.orgs().find(row => row.orgId === org)?.townContext,
        epoch,
      } : undefined;
      this.capabilities.refresh(org, `${sub}:${epoch}:${revision}`, true, townCodes, townScope);
    });

    // Demo authentication detection and preparation stay on /bff/demo/**. The first /api/me is
    // deliberately deferred until the BFF has auto-entered a persona; the server rejects every
    // root /api/** request on a configured demo host. Resume shell-owned initialization only after
    // the switcher has verified that persona and hydrated its core data.
    effect(() => {
      if (!this.personaSwitchHostApplies) return;
      const state = this.demoPersonaSwitcher.bootstrapState();
      const rootSessionDetected = this.demoPersonaSwitcher.rootSessionDetected();

      if (state === 'loading') {
        if (rootSessionDetected) {
          this.store.sessionChecked.set(true);
          this.sessionLoading.set(false);
        }
        return;
      }
      if (state === 'error' || state === 'signed-out' || state === 'unavailable') {
        // 'signed-out' (no authenticated BFF demo session) is the ordinary Keycloak OIDC sign-in
        // landing. 'error' after a detected root session renders the retry/sign-out gate, and
        // 'unavailable' (the probe itself failed) renders the gate's honest service-unavailable
        // face with retry -- see demoGateVisible().
        this.store.sessionChecked.set(true);
        this.sessionLoading.set(false);
        return;
      }
      if (state !== 'ready') return;

      const verifiedPersona = this.store.user();
      if (!verifiedPersona) return;
      this.completeDemoPostLogin(verifiedPersona);
    });

    // Balance-store resilience: a cash-position fetch that fails over the network must not cache
    // empty forever. store.loadPositions()'s own reload effect only re-fires on an org/entitlement
    // CHANGE (see store.ts), which a plain network hiccup never produces -- so without this, a
    // failed fetch would stay failed until the user switched org or persona. Retry centrally on
    // every navigation (not duplicated into every positions-consuming page component, per the
    // "fix the wiring, not per-page patches" rule) so the moment a user visits ANY page is exactly
    // when the platform tries again. untracked so this effect is keyed off navigation alone, NOT
    // off positionsError itself -- reading that signal directly here would make every flip of
    // positionsError re-run this effect and hammer a still-down endpoint with no backoff.
    effect(() => {
      this.currentUrl();
      untracked(() => this.store.retryPositionsIfFailed());
    });

    // ADR-0036 S7: when the factor gate is active, redirect to the DEDICATED enrollment
    // screen (not a modal). Carries the current URL as returnUrl so the user lands back
    // where they were once enrolled. The deferred routes (hardware-token order/tracking
    // and the enrollment screen itself) are excluded by isDeferredFactorEnrollmentRoute.
    effect(() => {
      if (this.showFactorEnrollmentGate()) {
        this.redirectToEnrollmentGate(this.currentUrl());
      }
    });
  }

  private redirectToEnrollmentGate(fromUrl: string): void {
    // safePostLoginUrl filters non-resumable paths (e.g. /signed-out, /error, /recovery/*)
    // and the enrollment screen itself — if the current URL is non-resumable, default to /home
    // so enrollment completion never bounces the user back to a broken or meaningless screen.
    const returnUrl = safePostLoginUrl(fromUrl !== '/login/enroll-factor' ? fromUrl : null);
    void this.router.navigate(['/login/enroll-factor'], { queryParams: { returnUrl } });
  }

  ngOnInit(): void {
    // A configured demo host must never probe the actual user through /api/me. The BFF-only
    // bootstrap probe detects the authenticated session, provisions the private instance, and
    // auto-enters a persona; DemoPersonaSwitchService calls /api/me only after that boundary.
    if (this.personaSwitchHostApplies) {
      this.store.beginEntitlementBootstrap();
      this.demoPersonaSwitcher.ensureLoaded();
      return;
    }
    // DEBUG/SANDBOX only: the wrapper may have just installed a real BFF cookie session via
    // /bff/debug/login. Still load identity from /api/me so the shell behaves like normal login.
    if (isDebugBuild) {
      this.loginViaBffSession();
      return;
    }
    if (skipsBootSessionProbe(globalThis.location?.pathname ?? '')) {
      this.store.user.set(null);
      this.store.sessionChecked.set(true);
      this.sessionLoading.set(false);
      return;
    }
    this.loginViaBffSession();
  }

  private loginViaBffSession(): void {
    this.store.loadMe().subscribe({
      next: (m) => {
        if (this.store.user()?.sub !== m.sub) this.store.resetForActualUserChange();
        this.store.user.set(m);
        this.store.sessionChecked.set(true);
        this.afterLogin(m);
      },
      error: () => {
        this.store.user.set(null);
        this.store.sessionChecked.set(true);
        this.sessionLoading.set(false);
      },
    });
  }

  private afterLogin(me: Me): void {
    this.sessionLoading.set(false);
    // Demo hosts must wait for /bff/demo/session to bind the persona token before any org-scoped
    // entitlement reads run. Non-demo hosts keep the normal immediate post-login data load.
    if (this.personaSwitchHostApplies) {
      if (this.demoPersonaSwitcher.readyFor(me)) {
        this.completeDemoPostLogin(me);
        return;
      }
      this.store.beginEntitlementBootstrap();
      if (this.demoPersonaSwitcher.bootstrapReady()) {
        // A service can outlive a nested AppComponent in the demo/debug wrappers. If a newly read
        // identity no longer matches that verified session, discard it and bootstrap this login.
        this.demoPersonaSwitcher.retryBootstrap();
      } else {
        this.demoPersonaSwitcher.ensureLoaded();
      }
      return;
    } else {
      this.store.finishEntitlementBootstrap();
      this.store.loadOrgs();
      this.personaSwitcher.ensureLoaded();
      this.armFactorEnrollmentGate();
    }
    this.finishPostLogin();
  }

  /**
   * ADR-0036 S7: arm the first-login factor gate for a freshly-authenticated ACTUAL user with no
   * ACTIVE step-up factor. Non-demo hosts only (this sits in afterLogin's non-demo branch): the
   * demo lane proves the authenticated actual/root user's factor readiness server-side before
   * entering a persona, and persona/acting sessions deliberately refuse /me/factors, so arming
   * there would both misfire and violate "personas are never MFA-gated". The retired Quick Start
   * compatibility path has its own pre-enrollment rule if deliberately reactivated. Fail-OPEN on
   * a factors-read error: an identity-service outage must not
   * lock every login behind an enrollment screen it cannot complete. Found 2026-08-19 while
   * building the first-login live e2e: every OTHER piece of the S7 gate existed (redirect
   * effect, exempt routes, dedicated screen, clear()) but nothing ever SET needsEnrollment — a
   * zero-factor user sailed straight into the portal and the gate had never once fired.
   *
   * EXPIRING_SOON counts as satisfying the gate too (factor-e2e lane, found the same day via the
   * new EXPIRING_SOON-last-factor e2e coverage): the whole point of that state is that the factor
   * is still usable for sign-in, distinct from EXPIRED. This mirrors the identity service's
   * FactorState.isEffectivelyActive and SecurityHubComponent's own isEffectivelyActive/
   * isOnlyActive (security-hub.component.ts), which already treat ACTIVE and EXPIRING_SOON
   * identically for "does this user have a usable factor". Before this fix, a user whose only
   * factor was EXPIRING_SOON (still valid, just due for renewal) was wrongly redirected to
   * /login/enroll-factor on every login as if they had zero factors. This check intentionally
   * stays narrower than that (ACTIVE only) inside the enrollment screen's own verifyEnrollment()
   * — a factor just freshly enrolled there is never already-expiring, so that distinction was
   * never wrong; only this pre-login gate was.
   */
  private armFactorEnrollmentGate(): void {
    // RC-F skip: this fires from afterLogin()'s non-demo-host branch, which also covers a page
    // reload while a real-customer VIEW_AS (or, defensively, any other acting) session is already
    // live — GET /api/identity/me/factors is deliberately refused (403) there. Skip firing it;
    // an acting context is unreachable without a gated root session that already passed this gate.
    if (actingContextSkipsFactorProbe(this.store.user()?.agencyMode)) return;
    this.factorService.listFactors().subscribe({
      next: (factors) => {
        if (!factors.some((f) => f.state === 'ACTIVE' || f.state === 'EXPIRING_SOON')) {
          this.enrollmentGate.needsEnrollment.set(true);
        }
      },
      error: () => {
        // fail-open by design — see method doc.
      },
    });
  }

  private completeDemoPostLogin(me: Me): void {
    const rootSubject = me.realSubject?.trim() || me.sub?.trim();
    if (!rootSubject || this.completedDemoRootSubject === rootSubject) return;
    this.completedDemoRootSubject = rootSubject;
    this.store.sessionChecked.set(true);
    this.sessionLoading.set(false);
    this.finishPostLogin();
  }

  /** Starts shell services only once the host-specific acting context is safe to use. */
  private finishPostLogin(): void {
    // The display preference is user-global, but the request still rides the active BFF session.
    // On demo hosts, do not ask for it through the root-only session while the private instance and
    // persona are still being established. This keeps every non-bootstrap API call behind the
    // same exact-identity + core-hydration boundary as the rest of the banking portal.
    this.masking.ensureLoaded?.();

    // Start notification history + SSE only after the host-specific acting context is ready.
    // On demo hosts this point follows private-instance and persona verification.
    this.notifService.init();

    // DEBUG-WRAPPER POST-LOGIN SANITIZER (fix: debug.demo.localhost /signed-out resume bug).
    //
    // On the debug host the login flow is: (1) browser navigates to the debug-shell at some URL
    // (e.g. debug.demo.localhost/signed-out after a logout), (2) the debug-shell shows the login
    // picker, (3) the user picks a persona and POSTs to /bff/debug/login, (4) enterPortal() mounts
    // <tb-root> — WITHOUT a page reload. This means the Angular router is still at whatever URL
    // the browser was on before the picker appeared (e.g. /signed-out).
    //
    // When <tb-root> mounts, the router activates the /signed-out route into the portal outlet
    // and the user sees the sign-out screen immediately after logging in. The BFF's
    // SanitizingPostLoginRedirectHandler never runs (there was no OIDC dance; /bff/debug/login
    // installs a BFF session directly). This SPA-side check mirrors the BFF denylist and
    // navigates to /home whenever afterLogin() fires with a non-resumable current URL.
    //
    // This guard is harmless on the customer host (demo.localhost) because there the OIDC flow
    // causes a full page reload and the browser URL at afterLogin() is always the BFF's redirect
    // target (/ or a legitimate deep link that was already sanitized by the BFF handler).
    //
    // DEEP-LINK INTENT RESUME (authShellGuard bounce fix): consume the sessionStorage stash the
    // guard wrote when it bounced an unauthenticated deep link to /signed-out. Consumed ONCE —
    // even when it loses to a real deep link — so a stale target can never replay on a later
    // login. See post-login-redirect.ts vector 4 for why sessionStorage (the BFF OIDC leg
    // cannot carry a SPA-supplied returnUrl).
    const resumeUrl = postLoginResumeUrl(consumePostLoginReturnUrl(), this.currentUrl());
    if (resumeUrl) {
      void this.router.navigateByUrl(resumeUrl);
    }

    // The actual user owns MFA. Plain SELF sessions perform the normal inventory probe. Persona
    // sessions skip an unreadable acting-session factor query only because their server-owned entry
    // seam already proved the actual/root user's factor readiness (OIDC demo entry or TownSim
    // pre-swap verification). The retired Quick Start compatibility path separately pre-enrolls a
    // factor if reactivated. This is not a sandbox waiver and never treats the persona as a
    // credential holder. A server-verified debug SELF session skips only the enrollment redirect;
    // its fake-device flow remains the sole synthetic operation-step-up proof.
    this.probeFactorEnrollment();
  }

  retryDemoBootstrap(): void {
    this.store.sessionChecked.set(false);
    this.sessionLoading.set(true);
    this.demoPersonaSwitcher.retryBootstrap();
  }

  /**
   * ADR-0036 S7: probe for active ToyBank factors. Federated first-logins and users whose
   * factors were all revoked have no ACTIVE factor — gate them before the main shell.
   * Persona reads self-skip only after their server-owned entry seam proved the actual/root user;
   * plain sessions still perform this probe directly.
   */
  private probeFactorEnrollment(): void {
    // The debug wrapper is the dev/test alternative to passkey enrollment. `debugSession` is
    // server-minted by the disabled-by-default, configured-host login wrapper; it is not an
    // acr/amr claim and never satisfies operation-bound step-up. Skip only the first-login
    // enrollment redirect here—the fake-device ceremony still models each real step-up UX.
    if (this.store.user()?.debugSession === true) {
      this.needsFactorEnrollment.set(false);
      return;
    }
    // RC-F skip (see actingContextSkipsFactorProbe): under an acting/persona context the read is
    // a guaranteed 403 by design — the probe's job was done at root login, and persona sessions
    // are unreachable without a gated root session. Don't fire the probe, don't leave the gate
    // armed. Plain sessions fall through to the byte-identical probe semantics below.
    if (actingContextSkipsFactorProbe(this.store.user()?.agencyMode)) {
      this.needsFactorEnrollment.set(false);
      return;
    }
    this.api.listIdentityFactors().subscribe({
      next: (factors) => {
        this.needsFactorEnrollment.set(!factors.some((f) => f.state === 'ACTIVE'));
      },
      error: () => {
        // Probe failed — do not block the user (fail-open). The step-up gate on each
        // sensitive action will catch any missing factor at action time.
        this.needsFactorEnrollment.set(false);
      },
    });
  }

  setOrg(orgId: string): void {
    if (this.townSession()) return;
    this.store.setOrg(orgId);
  }

  /** Show the org's human name, preferring demo session metadata when available. Never show the role. */
  orgLabel(org: OrgRef): string {
    return orgDisplayLabel(org, this.demoOrgName(), this.store.activeOrgId());
  }

  /**
   * Switch to a persona from the top-bar "Switch Demo User" menu. Closes the menu, then
   * delegates to the active switcher (BFF persona hop in the bff build, or the debug
   * wrapper's modeled login dance in the debug build). No-op when re-selecting the active one.
   */
  selectPersonaSwitch(key: string): void {
    this.personaSwitchOpen.set(false);
    if (this.personaSwitchActiveKey() === key) return;
    this.personaSwitcher.switchTo(key);
  }

  /**
   * ADR-0039 AI-2a: open the command assistant without leaving the current route.
   * Track 5 leg 2 (a11y): focus lands in the command input via the command box's own
   * autofocusOnInit (the box is created fresh each open by the panel @if).
   */
  openAssistant(): void {
    this.assistantOpen.set(true);
  }

  /**
   * Close the assistant and hand focus back to the launcher (a11y: the panel was opened from
   * it — close button / Escape / hotkey must not drop focus on <body>). Programmatic closes
   * that navigate away (navigateGuide / openHelpArticle) set the signal directly and do NOT
   * steal focus to the launcher.
   */
  closeAssistant(): void {
    this.dismissAssistant();
    const launcher = this.hostEl.nativeElement.querySelector(
      '[data-testid="assist-launcher-btn"]',
    ) as HTMLElement | null;
    launcher?.focus();
  }

  private dismissAssistant(): void {
    this.assistantOpen.set(false);
    this.assistDeliveryEpoch.update((epoch) => epoch + 1);
    this.pendingAssistCommand.set(null);
  }

  /**
   * Track 5 leg 2: global assistant hotkey — Ctrl+K (Cmd+K on mac) toggles the inline panel.
   * Guards: inert while any modal/dialog is up (aria-modal covers the intent modal, history
   * drawer, step-up + cosession dialogs) and while focus is in a text-entry control — UNLESS
   * that control is the assist command input itself (so the hotkey also closes). preventDefault
   * beats the browser's own Ctrl+K (search-bar focus) capture.
   */
  @HostListener('document:keydown', ['$event'])
  handleAssistantHotkey(event: KeyboardEvent): void {
    if (event.key !== 'k' && event.key !== 'K') return;
    if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
    if (!this.store.user()) return; // assistant shell only exists when signed in
    if (document.querySelector('[aria-modal="true"]')) return;
    const target = event.target;
    if (target instanceof HTMLElement && target.id !== 'assist-command-input') {
      const inTextEntry = target.closest('input, textarea, select') !== null || target.isContentEditable;
      if (inTextEntry) return;
    }
    event.preventDefault();
    if (this.assistantOpen()) {
      this.closeAssistant();
    } else {
      this.openAssistant();
    }
  }

  openAssistHistory(): void {
    this.assistHistoryOpen.set(true);
  }

  closeAssistHistory(): void {
    this.assistHistoryOpen.set(false);
  }

  /**
   * Track 5 leg 2 (history re-run): the drawer emitted a remembered command. Close the drawer,
   * make sure the assist panel is up, then fill + submit the command box — the same path the
   * bare-CLARIFY hint chips use (submitCommand). The setTimeout lets the zoneless render that
   * mounts <tb-command-box> complete first (the panel may have been closed).
   */
  rerunAssistCommand(command: string): void {
    this.assistHistoryOpen.set(false);
    this.assistantOpen.set(true);
    const context = this.assistCommandContext();
    setTimeout(() => this.deliverAssistCommand(command, true, context), 0);
  }

  /** Retain an invitation/history rerun until the lazy command box has mounted. */
  private deliverAssistCommand(command: string, submit: boolean, context = this.assistCommandContext()): void {
    if (!this.assistantOpen() || context !== this.assistCommandContext()) return;
    const box = this.shellCommandBox();
    if (box) {
      if (submit) box.submitCommand(command);
      else box.restoreCommand(command);
    } else {
      this.pendingAssistCommand.set({ text: command, submit, context });
    }
  }

  private assistCommandContext(): string {
    const user = this.store.user();
    return JSON.stringify([user?.realSubject, user?.sub, user?.memberHandle,
      this.store.activeOrgId(), this.store.personaEpoch(), this.assistDeliveryEpoch()]);
  }

  /** A command left the box — open a thinking turn and reset denial-follow-up state. */
  onShellSubmitted(command: string): void {
    this.shellDeniedQueryResult.set(null);
    this.shellDeniedQueryLoading.set(null);
    this.shellDeniedQueryError.set(null);
    this.shellPendingTurnId = this.assistThread.beginTurn(command);
  }

  /** The resolve round-trip failed — close the turn honestly (429/503/other). */
  onShellResolveFailed(failure: { status: number | null }): void {
    const turnId = this.shellPendingTurnId;
    this.shellPendingTurnId = null;
    if (!turnId) return;
    if (failure.status === 429) {
      this.assistThread.failTurn(turnId, 'rate-limited', 'Rate limit reached — give it a few seconds');
    } else if (failure.status === 503) {
      this.assistThread.failTurn(turnId, 'unavailable', 'The assistant service is unavailable right now');
    } else {
      this.assistThread.failTurn(turnId, 'generic', 'Something went wrong reaching the assistant');
    }
  }

  onShellResolved(response: ResolveIntentResponse): void {
    this.shellPendingResponse.set(null);
    this.shellDeniedResponse.set(null);
    this.shellEntitlementQueryResponse.set(null);
    this.shellRmReadResponse.set(null);
    this.shellAccountReadResponse.set(null);
    this.shellInsightsResponse.set(null);
    this.shellHelpResponse.set(null);
    this.shellGuideResponse.set(null);
    this.shellClarifyWithSuggestions.set(null);
    this.shellLastDecision.set(null);

    // Session-log thread: this resolve closes the thinking turn opened at submit time.
    // The classification below is recorded as the turn's state — same routing, now durable.
    const turnId = this.shellPendingTurnId;
    this.shellPendingTurnId = null;

    if (this.isShellHardDeny(response)) {
      this.shellDeniedResponse.set(response);
      if (turnId) this.assistThread.resolveTurn(turnId, response, 'denied');
      this.assistHistory.recordOutcome(response, 'declined');
      return;
    }

    if (isHelpResponse(response)) {
      this.shellHelpResponse.set(response);
      if (turnId) this.assistThread.resolveTurn(turnId, response, 'answered', 'Help article');
      this.assistHistory.recordOutcome(response, 'executed-read');
      return;
    }

    if (isGuideResponse(response)) {
      // ADR-0044 GUIDE route -- render as calm read-only step list with a deep-link.
      // Do NOT open the 3-way modal. No execute path, no step-up.
      this.shellGuideResponse.set(response);
      if (turnId) this.assistThread.resolveTurn(turnId, response, 'answered', 'Guide — how to get there');
      this.assistHistory.recordOutcome(response, 'executed-read');
      return;
    }

    if (isClarifyResponse(response)) {
      if (response.suggestedArticles && response.suggestedArticles.length > 0) {
        this.shellClarifyWithSuggestions.set(response);
      }
      if (turnId) this.assistThread.resolveTurn(turnId, response, 'clarify');
      this.assistHistory.recordOutcome(response, 'clarify');
      return;
    }

    if (isEntitlementQueryKind(response.actionKind)) {
      this.shellEntitlementQueryResponse.set(response);
      if (turnId) this.assistThread.resolveTurn(turnId, response, 'answered', 'Authority query');
      return;
    }

    if (isRmReadKind(response.actionKind) && response.data !== null && response.data !== undefined) {
      this.shellRmReadResponse.set(response);
      if (turnId) this.assistThread.resolveTurn(turnId, response, 'answered');
      this.assistHistory.recordOutcome(response, 'executed-read');
      return;
    }

    // Track 5 leg 3: CUSTOMER read kinds — the server already ran the read (entitlement-gated)
    // and relayed the payload in response.data. Render it inline; a READ must never reach the
    // 3-way modal / OBO execute path. Data-absent responses keep today's modal fallthrough.
    if (isAccountReadKind(response.actionKind) && response.data !== null && response.data !== undefined) {
      this.shellAccountReadResponse.set(response);
      if (turnId) this.assistThread.resolveTurn(turnId, response, 'answered');
      this.assistHistory.recordOutcome(response, 'executed-read');
      return;
    }

    if (isInsightsResponse(response)) {
      this.shellInsightsResponse.set(response);
      // An insights answer that could not apply a party/period the user named is logged as
      // Partial, carrying the SAME caveat the panel shows (insights-turn-outcome.ts).
      const insights = insightsTurnOutcome(response);
      if (turnId) {
        this.assistThread.resolveTurn(turnId, response, insights.state, insights.detail);
      }
      this.assistHistory.recordOutcome(response, 'executed-read');
      return;
    }

    // ACTION fall-through: the server-built prepared action awaits the user's confirm.
    if (turnId) this.assistThread.resolveTurn(turnId, response, 'prepared');
    this.shellPendingResponse.set(response);
  }

  shellDeniedGateKind(response: ResolveIntentResponse): string {
    return response.gates.find((gate) => gate.outcome === 'DENY')?.kind ?? '';
  }

  shellDeniedReason(response: ResolveIntentResponse): string {
    const gate = response.gates.find((g) => g.outcome === 'DENY');
    return gate?.detail?.trim()
      ? this.safeAssistantText(gate.detail, response)
      : 'The entitlement engine denied this action.';
  }

  displayAssistUserCommand(command: string): string {
    return displayUserCommand(command, this.masking.maskAccountNumbersByDefault());
  }

  safeAssistantText(
    text: string | null | undefined,
    response: ResolveIntentResponse | null | undefined = null,
  ): string {
    return assistantSafeText(text, response?.params ?? {});
  }

  private isShellHardDeny(response: ResolveIntentResponse): boolean {
    return !response.requiresStepUp && response.gates.some((gate) => gate.outcome === 'DENY');
  }

  onShellEntitlementQueryCompleted(event: AssistEntitlementQueryCompleted): void {
    this.assistThread.markByResponse(event.response, 'answered', 'Authority query answered');
    this.assistHistory.recordOutcome(event.response, 'executed-read');
  }

  onShellEntitlementQueryFailed(response: ResolveIntentResponse): void {
    this.assistThread.markByResponse(response, 'error', 'Authority query failed');
    this.assistHistory.recordOutcome(response, 'declined');
  }

  /**
   * Denial follow-up: names-only who-can roster for the denied action (mirrors the
   * intent modal's affordance — the engine answers, the SPA never computes authority).
   */
  launchShellWhoCan(denied: ResolveIntentResponse): void {
    const orgId = this.store.activeOrgId();
    const actionKind = this.shellQueryActionKind(denied);
    if (!orgId || !actionKind) {
      this.shellDeniedQueryError.set('Missing the organization or action needed for a who-can lookup.');
      return;
    }
    this.shellDeniedQueryLoading.set('WHO_CAN_QUERY');
    this.shellDeniedQueryError.set(null);
    this.shellDeniedQueryResult.set(null);
    this.assistQueryService.whoCanQuery(orgId, actionKind).subscribe({
      next: (result) => {
        this.shellDeniedQueryResult.set(result);
        this.shellDeniedQueryLoading.set(null);
      },
      error: () => {
        this.shellDeniedQueryError.set('Could not retrieve the who-can roster right now.');
        this.shellDeniedQueryLoading.set(null);
      },
    });
  }

  /** Denial follow-up: the full gate-trace explanation for why THIS user was denied. */
  launchShellExplainDenied(denied: ResolveIntentResponse): void {
    const orgId = this.store.activeOrgId();
    const subjectId = this.shellQuerySubjectId(denied);
    const actionKind = this.shellQueryActionKind(denied);
    if (!orgId || !subjectId || !actionKind) {
      this.shellDeniedQueryError.set('Missing the details needed to explain this decision.');
      return;
    }
    this.shellDeniedQueryLoading.set('EXPLAIN_DECISION');
    this.shellDeniedQueryError.set(null);
    this.shellDeniedQueryResult.set(null);
    this.assistQueryService.explainDecision({
      orgId,
      subjectId,
      actionKind,
      accountId: this.shellStringParam(denied, 'accountId', 'accountRef') || null,
      amount: this.shellAmountParam(denied),
      currency: this.shellStringParam(denied, 'currency') || null,
    }).subscribe({
      next: (result) => {
        this.shellDeniedQueryResult.set(result);
        this.shellDeniedQueryLoading.set(null);
      },
      // The BFF relays the entitlements service's own problem detail (code + detail) in the
      // query envelope's error. Replacing it with a generic sentence hides the one fact that
      // makes an unexplainable denial actionable, so the upstream message wins when present.
      error: (err: unknown) => {
        this.shellDeniedQueryError.set(explainDecisionErrorMessage(err));
        this.shellDeniedQueryLoading.set(null);
      },
    });
  }

  // Query-param extraction mirrors tb-command-intent-modal (server-populated params win,
  // then the response's own actionKind / the signed-in subject).
  private shellQueryActionKind(r: ResolveIntentResponse): string {
    return this.shellStringParam(r, 'action', 'actionKind') || r.actionKind;
  }

  private shellQuerySubjectId(r: ResolveIntentResponse): string {
    return (
      this.shellStringParam(r, 'subjectId', 'subject', 'actorSubject', 'principalSubject') ||
      (this.store.user()?.sub ?? '')
    );
  }

  private shellStringParam(r: ResolveIntentResponse, ...keys: string[]): string {
    for (const key of keys) {
      const value = r.params[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  private shellAmountParam(r: ResolveIntentResponse): string | null {
    const raw = r.params['amount'];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
    if (raw && typeof raw === 'object' && 'amount' in raw) {
      const amount = (raw as { amount?: unknown }).amount;
      if (typeof amount === 'string' && amount.trim()) return amount.trim();
      if (typeof amount === 'number' && Number.isFinite(amount)) return String(amount);
    }
    return null;
  }

  navigateGuide(guide: GuideRef): void {
    this.shellGuideResponse.set(null);
    const navigation = this.router.navigateByUrl(guide.deepLink);
    if (guide.stepTargets && guide.stepTargets.length > 0) {
      void navigation
        .then((navigated) => {
          if (navigated) {
            this.guideHighlight.showGuide(guide);
          }
        })
        .catch(() => undefined);
    }
    this.dismissAssistant();
  }

  dismissShellGuide(): void {
    this.shellGuideResponse.set(null);
  }

  dismissShellInsights(): void {
    this.shellInsightsResponse.set(null);
  }

  /**
   * ADR-0039 AI-2a: handle a decision from the global shell command modal.
   */
  onShellDecided(decision: AssistDecision): void {
    const previousDecision = this.shellLastDecision();
    if (decision.choice === 'INSTANT') {
      this.shellLastDecision.set(decision);
      this.assistThread.recordDecision(decision);
      this.assistHistory.recordOutcome(decision.response, this.historyOutcomeForDecision(decision));
    } else if (decision.choice === 'CANCELLED') {
      this.shellPendingResponse.set(null);
      if (this.shellShowMeHowResponses.has(decision.response)) {
        this.shellShowMeHowResponses.delete(decision.response);
        return;
      }
      if (this.shellRephraseResponses.has(decision.response)) {
        // Track 5: "Rephrase" is a hand-back to the command box, not a cancelled decision --
        // no decision chip, no history outcome.
        this.shellRephraseResponses.delete(decision.response);
        return;
      }
      if (!previousDecision || previousDecision.choice !== 'INSTANT') {
        this.shellLastDecision.set(decision);
        this.assistThread.recordDecision(decision);
        this.assistHistory.recordOutcome(decision.response, 'declined');
      }
    } else {
      this.shellPendingResponse.set(null);
      this.shellLastDecision.set(decision);
      this.assistThread.recordDecision(decision);
      this.assistHistory.recordOutcome(decision.response, 'declined');
    }
  }

  private historyOutcomeForDecision(decision: AssistDecision): AssistHistoryOutcome {
    switch (decision.routeKind) {
      case 'MONEY_PREFILL':
      case 'ENTITLEMENT_MODAL':
        return 'prepared-navigated-to-form';
      case 'DIRECT_ANSWER':
      case 'OBO_EXECUTE':
      case 'HELP':
        return 'executed-read';
      default:
        return 'executed-read';
    }
  }

  /**
   * Track 5: low-confidence "Rephrase" from the intent modal. Close the modal and hand the
   * user back to the command box with their original command text restored and selected.
   */
  onShellRephrase(response: ResolveIntentResponse): void {
    this.shellRephraseResponses.add(response);
    this.shellPendingResponse.set(null);
    this.assistThread.markByResponse(response, 'cancelled', 'Handed back to rephrase');
    const original = this.assistHistory.commandFor(response);
    this.shellCommandBox()?.restoreCommand(original ?? '');
  }

  openHelpArticle(article: HelpArticleRef): void {
    void this.router.navigate(['/help'], { queryParams: { article: article.id } });
    this.dismissAssistant();
  }

  dismissShellHelp(): void {
    this.shellHelpResponse.set(null);
  }

  /**
   * Sign the user out via the BFF Spring Security /logout endpoint.
   *
   * POST /logout invalidates the Spring Session in Redis, then RETURNS the URL the browser must go
   * to next ({@link LogoutResult}). With RP-initiated logout live that URL is Keycloak's
   * end_session endpoint, which ends the IdP SSO session and redirects back to /signed-out.
   *
   * We navigate with window.location (a TOP-LEVEL navigation) rather than letting the XHR follow a
   * redirect, because that destination is cross-origin and Keycloak's end_session endpoint sends no
   * CORS headers — an XHR following it would fail the fetch and trip the fail-closed branch below
   * on every successful sign-out. A top-level navigation is not a CORS request.
   *
   * If the server returns no URL (an older BFF, or RP-initiated logout switched off) we fall back
   * to the same-origin /signed-out landing, so sign-out degrades rather than breaks.
   * The CSRF token is carried automatically via the XSRF-TOKEN cookie by Angular's HttpClient.
   *
   * ADR-0003 security: tokens live server-side; logout invalidates the session cookie.
   *
   * Success and failure are handled DIFFERENTLY (defense-in-depth against a future server-side
   * regression): a prior version of this method navigated to /signed-out unconditionally, so a
   * failed server-side logout looked identical to a successful one and the session could silently
   * resume. On error we now (a) do NOT navigate — the client-side "you are logged out" state and
   * the actual server session must never diverge from what really happened, (b) leave
   * store.user/sessionChecked untouched so the authenticated shell (and this retry affordance)
   * keeps rendering, (c) surface a visible banner-stack error with a retry action
   * (banner-stack section below), and (d) log the failure. The success path is unchanged.
   */
  logout(): void {
    // Mashing sign-out fired N concurrent POSTs. The first invalidates the session, so races
    // 2..N 401 into the error branch and the user is shown "logout failed" AFTER a logout that
    // actually succeeded — the one outcome the error branch exists to prevent.
    if (this.loggingOut()) {
      return;
    }
    const reason = this.isPublicDemoHost ? 'demo' : 'user';
    this.logoutError.set(false);
    this.loggingOut.set(true);
    this.api.logout(reason).subscribe({
      next: (result) => {
        this.paymentDrafts.clearOnLogout();
        this.store.user.set(null);
        this.store.sessionChecked.set(true);
        this.sessionLoading.set(false);
        this.loggingOut.set(false);
        // Prefer the server-chosen destination (Keycloak end_session, which kills the SSO session
        // so the next person at this browser must re-enter a password). Falling back to the local
        // landing keeps sign-out working against a BFF that does not send one.
        window.location.href = result?.logoutUrl || `/signed-out?reason=${reason}`;
      },
      error: (err) => {
        console.error('[app] Logout failed -- session may still be active server-side; not navigating to /signed-out', err);
        this.loggingOut.set(false);
        this.logoutError.set(true);
      },
    });
  }

  private readonly paymentDrafts = inject(PaymentDraftService);

  /**
   * Called when the user clicks "Show me how" in the intent modal.
   * Re-queries the BFF with a "how do I" prefix to get a GUIDE response,
   * then renders the guide card directly in the shell assist panel.
   * SECURITY: this is a read-only navigation guide -- no execute path, no step-up.
   */
  onShellShowMeHow(response: ResolveIntentResponse): void {
    this.shellPendingResponse.set(null);
    this.shellShowMeHowResponses.add(response);
    this.assistThread.markByResponse(response, 'answered', 'Show me how — guide');
    // Build a natural-language guide query from the conversation-safe action summary.
    const guideQuery = assistantGuideQuery(response);
    // Lazy-import to keep the tree-shaking characteristics intact for production
    void import('./assist/assist.service').then(({ AssistService }) => {
      const svc = this.injector.get(AssistService);
      svc.resolveIntent(guideQuery).subscribe({
        next: (r) => {
          if (isGuideResponse(r)) {
            this.shellGuideResponse.set(r);
          } else {
            // Fallback: no GUIDE returned -- open the help article route
            void this.router.navigate(['/help'], { queryParams: { q: guideQuery } });
          }
        },
        error: () => {
          // Guide lookup failed silently -- fall through to help
          void this.router.navigate(['/help'], { queryParams: { q: guideQuery } });
        },
      });
    });
  }

  /**
   * The real portal's submit strategy for the shared <tb-login-screen> (see loginSubmitStrategy).
   * Forwards the shared component's optional username field as an OIDC `login_hint` -- edge-bff's
   * SecurityConfig#authRequestResolver already copies it onto the Keycloak authorization request
   * (no backend change needed), so a typed username pre-fills the hosted Keycloak login page's
   * username field exactly like the existing sandbox directory does (sandbox-shell.component.ts).
   * Blank is fine -- Keycloak still presents its own (passkey-first) login regardless.
   */
  login(): void {
    const hint = this.loginUsername().trim();
    const query = hint ? `?login_hint=${encodeURIComponent(hint)}` : '';
    window.location.href = `/oauth2/authorization/keycloak${query}`;
  }

  /** Initiate the passkey-preferred Keycloak login flow (Wave C). */
  loginWithPasskey(): void {
    window.location.href = this.passkeyService.passkeyLoginUrl();
  }
}
